import { getAppMode, getSquareConfig } from "../config";
import {
  deleteCurrentUser,
  getAuthSnapshot,
  getFirebaseIdToken,
  onAuthStateChange,
  signInWithGoogle,
  signInWithMagicLink,
  signOut,
  type AuthSnapshot,
} from "../lib/auth";
import { parsePhoneListText, parseUploadFile } from "../lib/csv";
import { usagePlans } from "../lib/defaults";
import {
  addAuthorizedUser,
  addRowsToBatch,
  addTestingAccount,
  checkIsTestingAccount,
  clearBatchData,
  clearPhoneNumberBlockers,
  createProfile,
  deleteAccountData,
  refillTokens,
  loadAppData,
  loadAuthorizedUsers,
  loadLinkedAccounts,
  loadNumberHistory,
  loadPhoneLog,
  loadTestingAccounts,
  markUploadReady,
  removeAuthorizedUser,
  removeRowFromBatch,
  saveBusinessProfile,
  saveBillingSetup,
  saveBillingActivation,
  saveManualBatch,
  savePlanSelection,
  saveValidatedUpload,
  loadUploadDetails,
} from "../lib/data";
import type {
  AccountAccess,
  AppData,
  BusinessAccount,
  BusinessProfile,
  CsvValidationResult,
  NumberHistoryEntry,
  PhoneLogEntry,
  ProfileAnalytics,
  ReviewPlatform,
  TestingAccount,
  UploadDetails,
  UploadRecord,
  UploadRow,
  UsagePlanId,
} from "../lib/types";

type RouteName =
  | "dashboard"
  | "profile"
  | "profile-select"
  | "upload"
  | "billing"
  | "log"
  | "numbers"
  | "team"
  | "test-accounts";

const routes: Array<{ name: RouteName; label: string; path: string }> = [
  { name: "dashboard", label: "Home", path: "/" },
  { name: "profile", label: "Profile", path: "/profile" },
  { name: "upload", label: "Upload", path: "/upload" },
  { name: "billing", label: "Billing", path: "/billing" },
  { name: "log", label: "Activity Log", path: "/log" },
  { name: "numbers", label: "Number History", path: "/numbers" },
  { name: "team", label: "Team", path: "/team" },
];

// Minimal Square Web Payments SDK types
interface SquareCard {
  attach(selector: string): Promise<void>;
  tokenize(): Promise<{ status: string; token?: string; errors?: Array<{ message: string }> }>;
  destroy(): Promise<void>;
}
interface SquarePayments {
  card(): Promise<SquareCard>;
}
declare global {
  interface Window { Square?: { payments(appId: string, locationId: string): Promise<SquarePayments> } }
}

// Lives outside state so it survives render() calls on non-billing pages,
// but is nulled out before every render to avoid stale iframe references.
let squareCard: SquareCard | null = null;
let squareInitCancelled = false;

const defaultReviewPlatforms: ReviewPlatform[] = [
  { id: "google", name: "Google", url: "", enabled: true },
  { id: "facebook", name: "Facebook", url: "", enabled: true },
];

const guestAccountStorageKey = "ratefollowup.guestAccountId";
const guestOwnerEmailStorageKey = "ratefollowup.guestOwnerEmail";

export function createApp(root: HTMLDivElement | null) {
  if (!root) {
    throw new Error("Application root not found.");
  }

  const mountNode = root;

  const state = {
    auth: {
      configured: false,
      session: null,
      user: null,
    } as AuthSnapshot,
    appData: null as AppData | null,
    route: resolveRoute(window.location.pathname),
    busy: false,
    loadingData: false,
    notice: "",
    error: "",
    csvResult: null as CsvValidationResult | null,
    csvFileName: "",
    lastSavedUploadId: "",
    selectedUploadDetails: null as UploadDetails | null,
    uploadAttested: false,
    billingShowUpdateForm: false,
    uploadMode: "file" as "file" | "manual",
    sendStatus: "idle" as "idle" | "sending" | "complete" | "failed" | "stopped",
    sendResult: null as SendResult | null,
    sendAbortController: null as AbortController | null,
    sendPollTimer: null as ReturnType<typeof setInterval> | null,
    sendPollTicks: 0,
    selectedProfileId: sessionStorage.getItem("selectedProfileId") as string | null,
    phoneLog: null as PhoneLogEntry[] | null,
    phoneLogLoading: false,
    logPhoneFilter: "" as string, // digits-only phone key; "" = all numbers
    pendingLogFilter: "" as string, // set by "view in log" drill-down, applied on next log load
    numberHistory: null as NumberHistoryEntry[] | null,
    numberHistoryLoading: false,
    numberHistoryFilter: "" as string, // search box text
    isTesting: false,
    activeAccountIsTesting: false,
    testingAccounts: null as TestingAccount[] | null,
    testingAccountsLoading: false,
    authorizedUsers: null as AccountAccess[] | null,
    authorizedUsersLoading: false,
    linkedAccounts: null as AccountAccess[] | null,
    guestAccountId: localStorage.getItem(guestAccountStorageKey) as string | null,
    guestOwnerEmail: localStorage.getItem(guestOwnerEmailStorageKey) as string | null,
  };

  let pendingPlatforms: ReviewPlatform[] = [...defaultReviewPlatforms];

  function setNotice(message = "") {
    state.notice = message;
  }

  function setError(message = "") {
    state.error = message;
  }

  function setBusy(value: boolean) {
    state.busy = value;
  }

  function navigate(path: string) {
    console.log(`[nav] ${window.location.pathname} → ${path}`);
    stopSendPoll();
    window.history.pushState({}, "", path);
    state.route = resolveRoute(path);
    render();
    if (path === "/log") {
      loadActivityLog();
    }
    if (path === "/numbers") {
      loadNumberHistoryPage();
    }
    if (path === "/test-accounts") {
      handleLoadTestingAccounts();
    }
    if (path === "/team") {
      handleLoadAuthorizedUsers();
    }
  }

  async function loadActivityLog() {
    const account = state.appData?.account;
    const profile = state.appData?.profile;
    if (!account || !profile) return;
    // A drill-down from Number History sets pendingLogFilter; otherwise the log
    // opens showing all numbers.
    const applyFilter = state.pendingLogFilter;
    state.pendingLogFilter = "";
    state.phoneLogLoading = true;
    state.logPhoneFilter = applyFilter;
    render();
    try {
      state.phoneLog = await loadPhoneLog(account.id, profile.id);
      console.log(`[log] loaded ${state.phoneLog.length} entries`);
    } catch (error) {
      console.error("[log] load failed:", error);
    } finally {
      state.phoneLogLoading = false;
      render();
    }
  }

  async function loadNumberHistoryPage() {
    const account = state.appData?.account;
    const profile = state.appData?.profile;
    if (!account || !profile) return;
    state.numberHistoryLoading = true;
    state.numberHistoryFilter = "";
    render();
    try {
      state.numberHistory = await loadNumberHistory(account.id, profile.id);
      console.log(`[numbers] loaded ${state.numberHistory.length} numbers`);
    } catch (error) {
      console.error("[numbers] load failed:", error);
    } finally {
      state.numberHistoryLoading = false;
      render();
    }
  }

  function handleViewNumberInLog(digits: string) {
    state.pendingLogFilter = digits;
    navigate("/log");
  }

  window.addEventListener("popstate", () => {
    state.route = resolveRoute(window.location.pathname);
    render();
    if (state.route === "test-accounts") {
      handleLoadTestingAccounts();
    }
    if (state.route === "team") {
      handleLoadAuthorizedUsers();
    }
  });

  async function refreshAppData() {
    if (!state.auth.user) {
      state.appData = null;
      state.isTesting = false;
      state.activeAccountIsTesting = false;
      return;
    }

    console.log(`[data] refreshAppData user=${state.auth.user.email} profileId=${state.selectedProfileId ?? "none"}`);
    state.loadingData = true;
    render();

    try {
      const accountIdToLoad = state.guestAccountId ?? state.auth.user.id;
      const [data, isTesting, linkedAccounts] = await Promise.all([
        loadAppData(
          accountIdToLoad,
          state.auth.user.email ?? null,
          state.selectedProfileId ?? undefined,
        ),
        checkIsTestingAccount(state.auth.user.email ?? ""),
        state.guestAccountId ? Promise.resolve(state.linkedAccounts ?? []) : loadLinkedAccounts(state.auth.user.email ?? ""),
      ]);
      let effectiveData = data;
      state.isTesting = isTesting;
      state.activeAccountIsTesting = isTesting;
      if (!state.guestAccountId) state.linkedAccounts = linkedAccounts;
      if (!state.guestAccountId && linkedAccounts.length === 1 && data.profiles.length === 0) {
        const access = linkedAccounts[0];
        state.guestAccountId = access.owner_account_id;
        state.guestOwnerEmail = access.owner_email;
        localStorage.setItem(guestAccountStorageKey, access.owner_account_id);
        localStorage.setItem(guestOwnerEmailStorageKey, access.owner_email);
        state.selectedProfileId = null;
        sessionStorage.removeItem("selectedProfileId");
        effectiveData = await loadAppData(
          access.owner_account_id,
          state.auth.user.email ?? null,
          undefined,
        );
        state.activeAccountIsTesting = await checkIsTestingAccount(access.owner_email);
      } else if (state.guestAccountId && state.guestOwnerEmail) {
        state.activeAccountIsTesting = await checkIsTestingAccount(state.guestOwnerEmail);
      }
      state.appData = effectiveData;
      const dataForRouting = effectiveData;
      console.log(`[data] loaded: ${dataForRouting.profiles.length} profile(s), selected=${dataForRouting.profile?.id ?? "none"}, uploads=${dataForRouting.uploads.length}, complete=${dataForRouting.profile?.is_profile_complete ?? false}`);

      if (!state.selectedProfileId) {
        if (dataForRouting.profiles.length === 0) {
          // New user — send to home for onboarding
          console.log("[data] no profiles → /");
          navigate("/");
        } else {
          // Has profiles — auto-select if exactly 1, then always send to locations page
          if (dataForRouting.profiles.length === 1) {
            state.selectedProfileId = dataForRouting.profiles[0].id;
            sessionStorage.setItem("selectedProfileId", dataForRouting.profiles[0].id);
            console.log(`[data] auto-selected only profile: ${dataForRouting.profiles[0].id}`);
            state.appData = await loadAppData(
              state.guestAccountId ?? state.auth.user.id,
              state.auth.user.email ?? null,
              state.selectedProfileId,
            );
          }
          console.log(`[data] has profiles → /profile-select`);
          navigate("/profile-select");
        }
      }
    } catch (error) {
      console.error("[data] refreshAppData failed:", error);
      setError(readError(error));
    } finally {
      state.loadingData = false;
      render();
    }
  }

  async function handleGoogleSignIn() {
    setBusy(true);
    setError();
    setNotice();
    render();

    try {
      await signInWithGoogle();
    } catch (error) {
      setError(readError(error));
      setBusy(false);
      render();
    }
  }

  async function handleMagicLink(event: SubmitEvent) {
    event.preventDefault();
    setBusy(true);
    setError();
    setNotice();
    render();

    const form = event.currentTarget as HTMLFormElement;
    const formData = new FormData(form);
    const email = String(formData.get("email") ?? "").trim();

    if (!email) {
      setError("Enter an email address.");
      setBusy(false);
      render();
      return;
    }

    try {
      await signInWithMagicLink(email);
      form.reset();
      setNotice("Magic link sent. Check your email to finish signing in.");
    } catch (error) {
      setError(readError(error));
    } finally {
      setBusy(false);
      render();
    }
  }

  async function handleSignOut() {
    console.log("[auth] sign out requested");
    setBusy(true);
    setError();
    setNotice();
    render();

    try {
      await signOut();
      state.appData = null;
      state.csvResult = null;
      state.csvFileName = "";
      state.lastSavedUploadId = "";
      state.selectedUploadDetails = null;
      state.guestAccountId = null;
      state.guestOwnerEmail = null;
      localStorage.removeItem(guestAccountStorageKey);
      localStorage.removeItem(guestOwnerEmailStorageKey);
      navigate("/");
    } catch (error) {
      setError(readError(error));
    } finally {
      setBusy(false);
      render();
    }
  }

  async function handleProfileSave(event: Event) {
    const form = mountNode.querySelector<HTMLFormElement>("[data-profile-form]");
    if (!form) return;
    const formData = new FormData(form);
    const platformsSnapshot = pendingPlatforms.map((p) => ({ ...p }));

    // Disable the button in-place without a full render so the form values
    // stay visible and pendingPlatforms is not reset mid-save.
    const submitBtn = event.currentTarget as HTMLButtonElement;
    submitBtn.disabled = true;
    submitBtn.textContent = "Saving…";

    setError();
    setNotice();

    try {
      const account = state.appData?.account;
      if (!account) throw new Error("Account not found. Please sign out and sign back in.");

      let profile = state.appData?.profile ?? null;
      if (!profile) {
        // Profile was deleted or never created — create one on the fly.
        const locationName = String(formData.get("location_name") ?? "").trim() || "My Business";
        profile = await createProfile(account.id, locationName);
        if (state.appData) {
          state.appData.profiles = [...(state.appData.profiles ?? []), profile];
          state.appData.profile = profile;
        }
        state.selectedProfileId = profile.id;
        sessionStorage.setItem("selectedProfileId", profile.id);
      }

      const updated = await saveBusinessProfile(account.id, profile.id, {
        location_name: String(formData.get("location_name") ?? "").trim() || profile.location_name,
        business_name: String(formData.get("business_name") ?? "").trim(),
        business_address: String(formData.get("business_address") ?? "").trim(),
        business_phone: String(formData.get("business_phone") ?? "").trim(),
        contact_name: String(formData.get("contact_name") ?? "").trim(),
        primary_email: String(formData.get("primary_email") ?? "").trim(),
        review_platforms: platformsSnapshot,
      });

      if (state.appData) state.appData.profile = updated;
      setNotice("Profile saved.");
      render();
      const savedBtn = mountNode.querySelector<HTMLButtonElement>("[data-save-profile]");
      if (savedBtn) {
        savedBtn.textContent = "✓ Saved";
        savedBtn.classList.add("profile-save-saved");
        setTimeout(() => {
          savedBtn.textContent = "Save profile";
          savedBtn.classList.remove("profile-save-saved");
        }, 2000);
      }
    } catch (error) {
      setError(readError(error));
      render();
      const failedBtn = mountNode.querySelector<HTMLButtonElement>("[data-save-profile]");
      if (failedBtn) {
        failedBtn.textContent = "Save failed";
        failedBtn.classList.add("profile-save-failed");
        setTimeout(() => {
          failedBtn.textContent = "Save profile";
          failedBtn.classList.remove("profile-save-failed");
        }, 3000);
      }
    }
  }

  async function handleFileSelected(event: Event) {
    const input = event.currentTarget as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    setBusy(true);
    setError();
    setNotice();
    state.csvResult = null;
    state.csvFileName = file.name;
    render();

    try {
      if (!state.appData?.account || !state.appData.profile) {
        throw new Error("Business profile is not available.");
      }

      const attestedBy = state.auth.user?.email ?? "";
      const validation = await parseUploadFile(file, attestedBy, state.activeAccountIsTesting);
      state.csvResult = validation;

      const savedUpload = await saveValidatedUpload(
        state.appData.account.id,
        state.appData.profile.id,
        state.auth.user?.id ?? "",
        file,
        validation,
      );

      state.lastSavedUploadId = savedUpload.upload.id;
      state.selectedUploadDetails = savedUpload;
      await refreshAppData();
      setNotice(`${validation.summary.validRows} numbers ready, ${validation.summary.rejectedRows} rejected.`);
    } catch (error) {
      setError(readError(error));
    } finally {
      setBusy(false);
      render();
    }
  }

  async function handleSelectUpload(event: Event) {
    const button = event.currentTarget as HTMLButtonElement;
    const uploadId = button.dataset.uploadId;
    const account = state.appData?.account;
    const profile = state.appData?.profile;
    if (!account || !profile || !uploadId) return;

    setBusy(true);
    setError();
    setNotice();
    render();

    try {
      state.selectedUploadDetails = await loadUploadDetails(account.id, profile.id, uploadId);
    } catch (error) {
      setError(readError(error));
    } finally {
      setBusy(false);
      render();
    }
  }

  async function handlePlanSelect(event: Event) {
    const button = event.currentTarget as HTMLButtonElement;
    const planId = button.dataset.planId as UsagePlanId | undefined;
    if (!state.appData?.account || !planId) return;

    setBusy(true);
    setError();
    setNotice();
    render();

    try {
      state.appData.account = await savePlanSelection(state.appData.account.id, planId);
      setNotice("Plan saved.");
    } catch (error) {
      setError(readError(error));
    } finally {
      setBusy(false);
      render();
    }
  }

  async function handleBillingSubmit() {
    const account = state.appData?.account;
    if (!account || !squareCard) return;

    const btn = mountNode.querySelector<HTMLButtonElement>("[data-billing-submit]");
    const errorEl = mountNode.querySelector<HTMLElement>("#billing-error");
    if (btn) { btn.disabled = true; btn.textContent = "Processing…"; }
    if (errorEl) { errorEl.style.display = "none"; errorEl.textContent = ""; }

    try {
      const result = await squareCard.tokenize();
      if (result.status !== "OK" || !result.token) {
        throw new Error(result.errors?.[0]?.message ?? "Card verification failed. Check your details.");
      }

      const idToken = await getFirebaseIdToken();
      if (!idToken) throw new Error("Authentication required.");

      const { apiBaseUrl } = getSquareConfig();
      const response = await fetch(`${apiBaseUrl}/v1/billing/setup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          source_id: result.token,
          account_id: account.id,
          email: state.auth.user?.email ?? "",
          existing_customer_id: account.square_customer_id ?? "",
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({})) as { error?: string };
        throw new Error(data.error ?? `Server error (${response.status})`);
      }

      const data = await response.json() as {
        customer_id: string;
        card_id: string;
        card_last4: string;
        card_brand: string;
      };

      const savedAccount = await saveBillingSetup(account.id, {
        square_customer_id: data.customer_id,
        square_card_id: data.card_id,
        card_last4: data.card_last4,
        card_brand: data.card_brand,
      });
      if (state.appData) state.appData.account = savedAccount;

      // Charge immediately and grant tokens for the first billing period
      const chargeResponse = await fetch(`${apiBaseUrl}/v1/billing/charge`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          account_id: account.id,
          customer_id: data.customer_id,
          card_id: data.card_id,
          plan_id: savedAccount.usage_plan_id,
        }),
      });

      if (!chargeResponse.ok) {
        const chargeData = await chargeResponse.json().catch(() => ({})) as { error?: string };
        throw new Error(chargeData.error ?? `Charge failed (${chargeResponse.status})`);
      }

      const today = new Date();
      const nextMonth = new Date(today);
      nextMonth.setDate(nextMonth.getDate() + 30);
      const plan = getUsagePlan(savedAccount.usage_plan_id);

      if (state.appData) state.appData.account = await saveBillingActivation(
        account.id,
        today.toISOString().slice(0, 10),
        nextMonth.toISOString().slice(0, 10),
        plan.includedMessages,
      );

      state.billingShowUpdateForm = false;
      setNotice("Payment method saved and plan activated.");
      render();
    } catch (error) {
      if (btn) { btn.disabled = false; btn.textContent = "Save payment method"; }
      if (errorEl) {
        errorEl.textContent = error instanceof Error ? error.message : "An unexpected error occurred.";
        errorEl.style.display = "";
      }
    }
  }

  async function initSquareBilling() {
    const container = mountNode.querySelector<HTMLElement>("#square-card-container");
    if (!container || squareCard) return;

    const config = getSquareConfig();
    if (!config.appId || config.appId.includes("REPLACE_ME")
      || !config.locationId || config.locationId.includes("REPLACE_ME")) {
      container.innerHTML = "<p class='muted'>Square credentials not configured — fill in VITE_SQUARE_APP_ID and VITE_SQUARE_LOCATION_ID in .env.dev1.</p>";
      return;
    }

    squareInitCancelled = false;

    if (!window.Square) {
      await new Promise<void>((resolve, reject) => {
        const script = document.createElement("script");
        script.src = config.sdkUrl;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error("Failed to load Square SDK"));
        document.head.appendChild(script);
      });
    }

    if (squareInitCancelled || !window.Square) return;

    try {
      const payments = await window.Square.payments(config.appId, config.locationId);
      if (squareInitCancelled) return;
      const card = await payments.card();
      if (squareInitCancelled) return;
      await card.attach("#square-card-container");
      squareCard = card;
    } catch (error) {
      if (!squareInitCancelled) {
        container.innerHTML = `<p class='muted'>Card form failed to load: ${error instanceof Error ? error.message : String(error)}</p>`;
      }
    }
  }

  async function handleDeleteAccount() {
    const accountId = state.appData?.account?.id;
    if (!accountId) return;

    const confirmed = window.confirm(
      "Delete all account data? This cannot be undone. Your login will also be removed so you can sign in fresh.",
    );
    if (!confirmed) return;

    setBusy(true);
    setError();
    setNotice();
    render();

    try {
      await deleteAccountData(accountId);
      await deleteCurrentUser();
      state.appData = null;
      state.csvResult = null;
      state.csvFileName = "";
      state.lastSavedUploadId = "";
      state.selectedUploadDetails = null;
      navigate("/");
    } catch (error) {
      setError(readError(error));
      setBusy(false);
      render();
    }
  }

  async function handleRefillTokens() {
    const accountId = state.appData?.account?.id;
    if (!accountId) return;

    setBusy(true);
    setError();
    setNotice();
    render();

    try {
      state.appData!.account = await refillTokens(accountId, 250);
      setNotice("Added 250 tokens.");
    } catch (error) {
      setError(readError(error));
    } finally {
      setBusy(false);
      render();
    }
  }

  async function handleClearBatches() {
    const account = state.appData?.account;
    const profile = state.appData?.profile;
    if (!account || !profile) return;

    const confirmed = window.confirm(
      "Delete all upload batches and phone history for this location? Your profile and billing stay intact.",
    );
    if (!confirmed) return;

    setBusy(true);
    setError();
    setNotice();
    render();

    try {
      await clearBatchData(account.id, profile.id);
      state.csvResult = null;
      state.csvFileName = "";
      state.lastSavedUploadId = "";
      state.selectedUploadDetails = null;
      state.sendStatus = "idle";
      state.sendResult = null;
      await refreshAppData();
      setNotice("All batches and phone history cleared.");
    } catch (error) {
      setError(readError(error));
    } finally {
      setBusy(false);
      render();
    }
  }

  async function handleClearPhoneBlockers(event: Event) {
    event.preventDefault();
    if (!state.isTesting) return;

    const account = state.appData?.account;
    const profile = state.appData?.profile;
    if (!account || !profile) {
      setError("Select a location before clearing a phone number.");
      render();
      return;
    }

    const form = (event.currentTarget as HTMLElement).closest("form") as HTMLFormElement | null;
    if (!form) return;

    const phonesRaw = String(new FormData(form).get("test_phone_number") ?? "").trim();
    if (!phonesRaw) {
      setError("Enter one or more phone numbers to clear.");
      render();
      return;
    }
    // Accept several numbers separated by commas, spaces, or new lines.
    const phoneInputs = phonesRaw.split(/[\s,]+/).filter(Boolean);

    setBusy(true);
    setError();
    setNotice();
    render();

    try {
      const { cleared, invalid } = await clearPhoneNumberBlockers(account.id, profile.id, phoneInputs);
      form.reset();
      await refreshAppData();
      const parts = [`Cleared blockers for ${cleared.length} number${cleared.length === 1 ? "" : "s"}: ${cleared.join(", ")}`];
      if (invalid.length > 0) parts.push(`skipped ${invalid.length} invalid (${invalid.join(", ")})`);
      setNotice(`${parts.join("; ")}. Phone log was kept.`);
    } catch (error) {
      setError(readError(error));
    } finally {
      setBusy(false);
      render();
    }
  }

  async function handleLoadTestingAccounts() {
    if (!state.isTesting) return;
    state.testingAccountsLoading = true;
    render();
    try {
      state.testingAccounts = await loadTestingAccounts();
    } catch (error) {
      setError(readError(error));
    } finally {
      state.testingAccountsLoading = false;
      render();
    }
  }

  async function handleAddTestingAccount(event: Event) {
    event.preventDefault();
    if (!state.isTesting || !state.auth.user?.email) return;
    const form = (event.currentTarget as HTMLElement).closest("form") as HTMLFormElement | null;
    if (!form) return;
    const email = String(new FormData(form).get("new_testing_email") ?? "").trim().toLowerCase();
    if (!email) return;

    setBusy(true);
    setError();
    render();
    try {
      await addTestingAccount(email, state.auth.user.email);
      form.reset();
      state.testingAccounts = await loadTestingAccounts();
      setNotice(`${email} added as a testing account.`);
    } catch (error) {
      setError(readError(error));
    } finally {
      setBusy(false);
      render();
    }
  }

  async function handleLoadAuthorizedUsers() {
    const account = state.appData?.account;
    if (!account || state.guestAccountId) return;
    state.authorizedUsersLoading = true;
    render();
    try {
      state.authorizedUsers = await loadAuthorizedUsers(account.id);
    } catch (error) {
      setError(`Failed to load team members: ${readError(error)}`);
    } finally {
      state.authorizedUsersLoading = false;
      render();
    }
  }

  async function handleAddAuthorizedUser(event: Event) {
    event.preventDefault();
    const account = state.appData?.account;
    const ownerEmail = state.auth.user?.email;
    if (!account || !ownerEmail || state.guestAccountId) return;
    const form = (event.currentTarget as HTMLElement).closest("form") as HTMLFormElement | null;
    if (!form) return;
    const email = String(new FormData(form).get("new_authorized_email") ?? "").trim().toLowerCase();
    if (!email) {
      setError("Enter an email address before clicking Add to team.");
      render();
      return;
    }

    setBusy(true);
    setError();
    render();
    try {
      await addAuthorizedUser(account.id, ownerEmail, email, ownerEmail);
      form.reset();
      state.authorizedUsers = await loadAuthorizedUsers(account.id);
      setNotice(`${email} added to your team.`);
    } catch (error) {
      setError(`Failed to add team member: ${readError(error)}`);
    } finally {
      setBusy(false);
      render();
    }
  }

  async function handleRemoveAuthorizedUser(email: string) {
    const account = state.appData?.account;
    if (!account || state.guestAccountId) return;
    setBusy(true);
    setError();
    render();
    try {
      await removeAuthorizedUser(account.id, email);
      state.authorizedUsers = await loadAuthorizedUsers(account.id);
      setNotice(`${email} removed from your team.`);
    } catch (error) {
      setError(readError(error));
    } finally {
      setBusy(false);
      render();
    }
  }

  async function handleSwitchToGuestAccount(access: AccountAccess) {
    state.guestAccountId = access.owner_account_id;
    state.guestOwnerEmail = access.owner_email;
    localStorage.setItem(guestAccountStorageKey, access.owner_account_id);
    localStorage.setItem(guestOwnerEmailStorageKey, access.owner_email);
    state.selectedProfileId = null;
    sessionStorage.removeItem("selectedProfileId");
    setNotice(`Switched to ${access.owner_email}'s account.`);
    await refreshAppData();
  }

  function handleSwitchBackToOwnAccount() {
    state.guestAccountId = null;
    state.guestOwnerEmail = null;
    localStorage.removeItem(guestAccountStorageKey);
    localStorage.removeItem(guestOwnerEmailStorageKey);
    state.selectedProfileId = null;
    sessionStorage.removeItem("selectedProfileId");
    setNotice("Switched back to your account.");
    refreshAppData();
  }

  async function handleSendAll() {
    if (!state.appData?.account || !state.appData.profile) {
      setError("No profile selected. Try refreshing the page.");
      render();
      return;
    }

    const account = state.appData.account;
    const profile = state.appData.profile;

    // "validated" uploads need markUploadReady first; "ready" uploads already have queued rows
    const validatedUploads = state.appData.uploads.filter(
      (u) => u.status === "validated" && u.valid_rows > 0,
    );
    const readyUploads = state.appData.uploads.filter(
      (u) => u.status === "ready" && u.queued_rows > 0,
    );
    const allSendable = [...validatedUploads, ...readyUploads];

    if (allSendable.length === 0) {
      setError(`No batches ready to send. Batch statuses: ${state.appData.uploads.map((u) => `${u.status}(queued=${u.queued_rows})`).join(", ") || "none"}`);
      render();
      return;
    }

    if (!state.activeAccountIsTesting && (!account.square_card_id || account.billing_status !== "active")) {
      setError(`Billing not active (status: ${account.billing_status}). Set up billing before sending.`);
      render();
      return;
    }

    console.log(`[send] starting — ${allSendable.length} batch(es), account=${account.id}, profile=${profile.id}`);
    setBusy(true);
    setError();
    setNotice();
    state.sendStatus = "sending";
    state.sendResult = null;
    state.sendAbortController = new AbortController();
    render();

    try {
      // Stage any validated uploads (applies suppression window, sets rows to "queued")
      for (const upload of validatedUploads) {
        console.log(`[send] staging upload ${upload.id}`);
        await markUploadReady(account.id, profile.id, upload.id);
      }

      // Tell Go to send — it reads all rows with status "queued" for each upload
      const idToken = await getFirebaseIdToken();
      if (!idToken) throw new Error("Authentication required.");
      console.log(`[send] calling /v1/sms/send-batch with ${allSendable.length} upload(s)`);

      const { apiBaseUrl } = getSquareConfig();
      const resp = await fetch(`${apiBaseUrl}/v1/sms/send-batch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          account_id: account.id,
          profile_id: profile.id,
          upload_ids: allSendable.map((u) => u.id),
        }),
        signal: state.sendAbortController.signal,
      });

      if (!resp.ok) {
        const data = await resp.json().catch(() => ({})) as { error?: string };
        throw new Error(data.error ?? `Send failed (${resp.status})`);
      }

      const result = await resp.json() as SendResult;
      console.log(`[send] enqueued=${result.enqueued}, skipped=${result.skipped}, failed=${result.failed}, total=${result.total}`);
      result.results?.forEach((r) => {
        if (r.status !== "enqueued") console.warn(`[send] ${r.phone}: ${r.status}${r.reason ? " — " + r.reason : ""}`);
      });
      // The worker sends the messages and bills tokens as it goes; the client no
      // longer deducts tokens. The balance updates as the poll refreshes data.
      state.sendResult = result;
      state.sendStatus = result.failed > 0 && result.enqueued === 0 ? "failed" : "complete";
      await refreshAppData();
      // Rows now advance in the background (opt-in → response → link). Poll so the
      // batch table and token balance reflect the worker's progress.
      if (result.enqueued > 0) startSendPoll();
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        console.log("[send] cancelled by user");
        state.sendStatus = "stopped";
      } else {
        console.error("[send] batch failed:", error);
        setError(readError(error));
        state.sendStatus = "failed";
      }
    } finally {
      state.sendAbortController = null;
      setBusy(false);
      render();
    }
  }

  function handleStopSend() {
    state.sendAbortController?.abort();
  }

  // Background progress poll. After enqueue the worker drives each row forward
  // (opt-in → response → link) and bills tokens; polling reloads app data so the
  // batch table and balance track that without a manual refresh. Bounded so it
  // never runs forever; the user can leave the page at any time (stopSendPoll).
  const sendPollIntervalMs = 10_000;
  const sendPollMaxTicks = 30; // ~5 minutes

  function startSendPoll() {
    stopSendPoll();
    state.sendPollTicks = 0;
    state.sendPollTimer = setInterval(pollSendProgress, sendPollIntervalMs);
  }

  function stopSendPoll() {
    if (state.sendPollTimer !== null) {
      clearInterval(state.sendPollTimer);
      state.sendPollTimer = null;
    }
  }

  async function pollSendProgress() {
    state.sendPollTicks++;
    if (state.sendPollTicks > sendPollMaxTicks || !state.auth.user || !state.appData?.profile) {
      stopSendPoll();
      return;
    }
    try {
      const data = await loadAppData(
        state.guestAccountId ?? state.auth.user.id,
        state.auth.user.email ?? null,
        state.selectedProfileId ?? undefined,
      );
      state.appData = data;
      render();
    } catch (error) {
      console.warn("[send] poll failed:", error);
    }
  }

  async function handleManualAdd() {
    const account = state.appData?.account;
    const profile = state.appData?.profile;
    if (!account || !profile) return;

    const textarea = mountNode.querySelector<HTMLTextAreaElement>("[data-manual-text]");
    const text = textarea?.value.trim() ?? "";
    if (!text) {
      setError("Enter at least one phone number.");
      render();
      return;
    }

    setBusy(true);
    setError();
    setNotice();
    render();

    try {
      const attestedBy = state.auth.user?.email ?? "";
      const validation = parsePhoneListText(text, attestedBy, state.activeAccountIsTesting);
      const saved = await saveManualBatch(account.id, profile.id, validation);
      state.selectedUploadDetails = saved;
      state.lastSavedUploadId = saved.upload.id;
      state.uploadMode = "file";
      await refreshAppData();
      setNotice(`${validation.summary.validRows} number${validation.summary.validRows === 1 ? "" : "s"} ready, ${validation.summary.rejectedRows} rejected.`);
    } catch (error) {
      setError(readError(error));
    } finally {
      setBusy(false);
      render();
    }
  }

  async function handleAddMoreRows(uploadId: string) {
    const account = state.appData?.account;
    const profile = state.appData?.profile;
    if (!account || !profile) return;

    const textarea = mountNode.querySelector<HTMLTextAreaElement>("[data-add-more-text]");
    const text = textarea?.value.trim() ?? "";
    if (!text) {
      setError("Enter at least one phone number.");
      render();
      return;
    }

    setBusy(true);
    setError();
    setNotice();
    render();

    try {
      const attestedBy = state.auth.user?.email ?? "";
      const validation = parsePhoneListText(text, attestedBy, state.activeAccountIsTesting);
      state.selectedUploadDetails = await addRowsToBatch(account.id, profile.id, uploadId, validation);
      await refreshAppData();
      setNotice(`Added ${validation.summary.validRows} number${validation.summary.validRows === 1 ? "" : "s"}.`);
    } catch (error) {
      setError(readError(error));
    } finally {
      setBusy(false);
      render();
    }
  }

  async function handleRemoveRow(uploadId: string, rowId: string) {
    const account = state.appData?.account;
    const profile = state.appData?.profile;
    if (!account || !profile) return;

    setBusy(true);
    setError();
    setNotice();
    render();

    try {
      state.selectedUploadDetails = await removeRowFromBatch(account.id, profile.id, uploadId, rowId);
      await refreshAppData();
    } catch (error) {
      setError(readError(error));
    } finally {
      setBusy(false);
      render();
    }
  }

  async function handleSelectProfile(profileId: string) {
    console.log(`[profile] selecting ${profileId}`);
    state.selectedProfileId = profileId;
    sessionStorage.setItem("selectedProfileId", profileId);
    await refreshAppData();
    navigate("/");
  }

  async function handleCreateProfile(event: SubmitEvent) {
    event.preventDefault();
    const account = state.appData?.account;
    if (!account) return;

    const form = event.currentTarget as HTMLFormElement;
    const locationName = String(new FormData(form).get("location_name") ?? "").trim();
    if (!locationName) {
      setError("Enter a location name.");
      render();
      return;
    }

    console.log(`[profile] creating location "${locationName}"`);
    setBusy(true);
    setError();
    setNotice();
    render();

    try {
      const profile = await createProfile(account.id, locationName);
      console.log(`[profile] created ${profile.id} ("${profile.location_name}")`);
      state.selectedProfileId = profile.id;
      sessionStorage.setItem("selectedProfileId", profile.id);
      await refreshAppData();
      navigate("/profile");
    } catch (error) {
      console.error("[profile] create failed:", error);
      setError(readError(error));
      setBusy(false);
      render();
    }
  }

  function handleSwitchProfile() {
    console.log("[profile] switching profile — clearing selection");
    state.selectedProfileId = null;
    sessionStorage.removeItem("selectedProfileId");
    navigate("/profile-select");
  }

  function bindEvents() {
    mountNode.querySelectorAll<HTMLAnchorElement>("[data-nav]").forEach((node) => {
      node.addEventListener("click", (event) => {
        event.preventDefault();
        navigate(node.getAttribute("href") || "/");
      });
    });

    mountNode
      .querySelector<HTMLButtonElement>("[data-google-login]")
      ?.addEventListener("click", handleGoogleSignIn);

    mountNode
      .querySelector<HTMLFormElement>("[data-magic-link-form]")
      ?.addEventListener("submit", handleMagicLink);

    mountNode
      .querySelector<HTMLButtonElement>("[data-sign-out]")
      ?.addEventListener("click", handleSignOut);

    mountNode
      .querySelector<HTMLButtonElement>("[data-save-profile]")
      ?.addEventListener("click", handleProfileSave);

    mountNode
      .querySelector<HTMLInputElement>("[data-attest-checkbox]")
      ?.addEventListener("change", (event) => {
        state.uploadAttested = (event.currentTarget as HTMLInputElement).checked;
        render();
      });

    mountNode
      .querySelector<HTMLInputElement>("[data-file-input]")
      ?.addEventListener("change", handleFileSelected);

    mountNode.querySelectorAll<HTMLButtonElement>("[data-upload-tab]").forEach((btn) => {
      btn.addEventListener("click", () => {
        state.uploadMode = btn.dataset.uploadTab as "file" | "manual";
        render();
      });
    });

    mountNode
      .querySelector<HTMLButtonElement>("[data-manual-add]")
      ?.addEventListener("click", handleManualAdd);

    mountNode
      .querySelector<HTMLButtonElement>("[data-add-more-rows]")
      ?.addEventListener("click", () => {
        const btn = mountNode.querySelector<HTMLButtonElement>("[data-add-more-rows]");
        const uploadId = btn?.dataset.addMoreRows ?? "";
        if (uploadId) handleAddMoreRows(uploadId);
      });

    mountNode.querySelectorAll<HTMLButtonElement>("[data-remove-row]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const uploadId = btn.dataset.removeRow ?? "";
        const rowId = btn.dataset.rowId ?? "";
        if (uploadId && rowId) handleRemoveRow(uploadId, rowId);
      });
    });

    mountNode.querySelectorAll<HTMLButtonElement>("[data-select-upload]").forEach((node) => {
      node.addEventListener("click", handleSelectUpload);
    });

    mountNode.querySelectorAll<HTMLButtonElement>("[data-plan-id]").forEach((node) => {
      node.addEventListener("click", handlePlanSelect);
    });

    mountNode
      .querySelector<HTMLButtonElement>("[data-send-all]")
      ?.addEventListener("click", handleSendAll);

    mountNode
      .querySelector<HTMLButtonElement>("[data-stop-send]")
      ?.addEventListener("click", handleStopSend);

    mountNode.querySelectorAll<HTMLButtonElement>("[data-toggle-raw]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const wrapper = btn.closest(".table-wrapper");
        if (!wrapper) return;
        const showing = wrapper.classList.toggle("show-raw");
        btn.textContent = showing ? "Hide original numbers" : "Show original numbers";
      });
    });

    mountNode.querySelectorAll<HTMLButtonElement>("[data-select-profile]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const profileId = btn.dataset.selectProfile ?? "";
        if (profileId) handleSelectProfile(profileId);
      });
    });

    mountNode
      .querySelector<HTMLFormElement>("[data-create-profile-form]")
      ?.addEventListener("submit", handleCreateProfile);

    mountNode
      .querySelector<HTMLButtonElement>("[data-switch-profile]")
      ?.addEventListener("click", handleSwitchProfile);

    mountNode
      .querySelector<HTMLButtonElement>("[data-delete-account]")
      ?.addEventListener("click", handleDeleteAccount);

    mountNode
      .querySelector<HTMLButtonElement>("[data-clear-batches]")
      ?.addEventListener("click", handleClearBatches);

    mountNode
      .querySelector<HTMLButtonElement>("[data-refill-tokens]")
      ?.addEventListener("click", handleRefillTokens);

    mountNode
      .querySelector<HTMLSelectElement>("[data-log-phone-filter]")
      ?.addEventListener("change", (event) => {
        state.logPhoneFilter = (event.currentTarget as HTMLSelectElement).value;
        render();
      });

    mountNode
      .querySelector<HTMLInputElement>("[data-number-search]")
      ?.addEventListener("input", (event) => {
        const el = event.currentTarget as HTMLInputElement;
        const pos = el.selectionStart;
        state.numberHistoryFilter = el.value;
        render();
        // Re-render replaces the DOM, so restore focus + caret to the search box.
        const again = mountNode.querySelector<HTMLInputElement>("[data-number-search]");
        if (again) {
          again.focus();
          if (pos != null) again.setSelectionRange(pos, pos);
        }
      });

    mountNode
      .querySelectorAll<HTMLButtonElement>("[data-view-number]")
      .forEach((node) => {
        node.addEventListener("click", () => {
          handleViewNumberInLog(node.dataset.viewNumber ?? "");
        });
      });

    mountNode
      .querySelector<HTMLButtonElement>("[data-clear-phone-blockers]")
      ?.addEventListener("click", handleClearPhoneBlockers);

    mountNode
      .querySelector<HTMLButtonElement>("[data-billing-submit]")
      ?.addEventListener("click", handleBillingSubmit);

    mountNode
      .querySelector<HTMLButtonElement>("[data-add-testing-account]")
      ?.addEventListener("click", handleAddTestingAccount);

    mountNode
      .querySelector<HTMLButtonElement>("[data-add-authorized-user]")
      ?.addEventListener("click", handleAddAuthorizedUser);

    mountNode.querySelectorAll<HTMLButtonElement>("[data-remove-authorized-user]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const email = btn.dataset.removeAuthorizedUser ?? "";
        if (email) handleRemoveAuthorizedUser(email);
      });
    });

    mountNode.querySelectorAll<HTMLButtonElement>("[data-switch-guest-account]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const idx = Number(btn.dataset.switchGuestAccount ?? "0");
        const access = state.linkedAccounts?.[idx];
        if (access) handleSwitchToGuestAccount(access);
      });
    });

    mountNode
      .querySelector<HTMLButtonElement>("[data-switch-own-account]")
      ?.addEventListener("click", handleSwitchBackToOwnAccount);

    mountNode
      .querySelector<HTMLButtonElement>("[data-update-card]")
      ?.addEventListener("click", () => {
        state.billingShowUpdateForm = true;
        render();
      });

    if (state.route === "billing" && mountNode.querySelector("#square-card-container")) {
      initSquareBilling().catch(() => {});
    }

    // Live profile field badge updates
    const profileForm = mountNode.querySelector<HTMLFormElement>("[data-profile-form]");
    if (profileForm) {
      function setFieldBadge(key: string, filled: boolean) {
        const badge = profileForm!.querySelector<HTMLElement>(`[data-field-badge="${key}"]`);
        if (!badge) return;
        badge.className = `field-status-badge ${filled ? "badge-done" : "badge-needed"}`;
        badge.textContent = filled ? "✓" : "✗";
      }

      function syncPlatformBadge() {
        const any = pendingPlatforms.some((p) => p.enabled && p.url.trim());
        setFieldBadge("review_platforms", any);
      }

      for (const name of ["business_name", "business_address", "business_phone", "contact_name", "primary_email"]) {
        profileForm.querySelector<HTMLInputElement | HTMLTextAreaElement>(`[name="${name}"]`)
          ?.addEventListener("input", (e) =>
            setFieldBadge(name, Boolean((e.currentTarget as HTMLInputElement).value.trim())),
          );
      }

      bindPlatformEditorEvents(syncPlatformBadge);
    }
  }

  function bindPlatformEditorEvents(syncBadge: () => void) {
    const editor = mountNode.querySelector<HTMLElement>("[data-platform-editor]");
    if (!editor) return;

    editor.querySelectorAll<HTMLInputElement>("[data-toggle-platform]").forEach((cb) => {
      cb.addEventListener("change", () => {
        const idx = Number(cb.dataset.togglePlatform);
        if (pendingPlatforms[idx]) pendingPlatforms[idx].enabled = cb.checked;
        syncBadge();
      });
    });

    editor.querySelectorAll<HTMLInputElement>("[data-platform-url]").forEach((input) => {
      input.addEventListener("input", () => {
        const idx = Number(input.dataset.platformUrl);
        if (pendingPlatforms[idx]) pendingPlatforms[idx].url = input.value;
        syncBadge();
      });
    });

    editor.querySelectorAll<HTMLInputElement>("[data-platform-name]").forEach((input) => {
      input.addEventListener("input", () => {
        const idx = Number(input.dataset.platformName);
        if (pendingPlatforms[idx]) pendingPlatforms[idx].name = input.value;
      });
    });

    editor.querySelectorAll<HTMLButtonElement>("[data-remove-platform]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const idx = Number(btn.dataset.removePlatform);
        pendingPlatforms.splice(idx, 1);
        refreshPlatformEditor(syncBadge);
      });
    });

    mountNode.querySelector<HTMLButtonElement>("[data-add-platform]")
      ?.addEventListener("click", () => {
        pendingPlatforms.push({ id: `custom_${Date.now()}`, name: "Custom", url: "", enabled: true });
        refreshPlatformEditor(syncBadge);
      });
  }

  function refreshPlatformEditor(syncBadge: () => void) {
    const editor = mountNode.querySelector<HTMLElement>("[data-platform-editor]");
    if (!editor) return;
    editor.innerHTML = renderPlatformEditorRows(pendingPlatforms);
    bindPlatformEditorEvents(syncBadge);
    syncBadge();
  }

  function render() {
    squareInitCancelled = true;
    if (squareCard) {
      squareCard.destroy().catch(() => {});
      squareCard = null;
    }

    const pathname = window.location.pathname;

    // Public routes — bypass auth entirely
    if (pathname.startsWith("/r/") || pathname.startsWith("/f/")) {
      mountNode.innerHTML = renderPublicShell(pathname);
      initPublicPage(pathname);
      return;
    }

    // Reset pending platforms each time the profile page renders from scratch
    if (state.route === "profile") {
      const saved = state.appData?.profile?.review_platforms;
      pendingPlatforms = saved && saved.length > 0
        ? saved.map((p) => ({ ...p }))
        : [...defaultReviewPlatforms];
    }

    mountNode.innerHTML = state.auth.user
      ? renderAuthedApp({ ...state, pendingPlatforms, selectedProfileId: state.selectedProfileId, phoneLog: state.phoneLog, phoneLogLoading: state.phoneLogLoading, isTesting: state.isTesting, activeAccountIsTesting: state.activeAccountIsTesting, testingAccounts: state.testingAccounts, testingAccountsLoading: state.testingAccountsLoading, authorizedUsers: state.authorizedUsers, authorizedUsersLoading: state.authorizedUsersLoading, linkedAccounts: state.linkedAccounts, guestAccountId: state.guestAccountId, guestOwnerEmail: state.guestOwnerEmail })
      : renderAuthLanding(state);
    bindEvents();
  }

  function initPublicPage(pathname: string) {
    const { apiBaseUrl } = getSquareConfig();
    if (pathname.startsWith("/r/")) {
      const token = pathname.slice("/r/".length);
      fetchReviewToken(apiBaseUrl, token);
    } else if (pathname.startsWith("/f/")) {
      const token = pathname.slice("/f/".length);
      fetchFeedbackToken(apiBaseUrl, token);
      bindPublicFeedbackForm(apiBaseUrl, token);
    }
  }

  async function fetchReviewToken(apiBaseUrl: string, token: string) {
    const container = mountNode.querySelector<HTMLElement>("#public-content");
    if (!container) return;
    try {
      const resp = await fetch(`${apiBaseUrl}/v1/r/${encodeURIComponent(token)}`);
      if (!resp.ok) throw new Error("Review link not found or expired.");
      const data = await resp.json() as { business_name: string; location_name: string; platforms: Array<{ name: string; url: string }> };

      const locationLine = data.location_name
        ? `<div class="review-location">${escapeHtml(data.location_name)}</div>`
        : "";

      container.innerHTML = `
        <div class="review-business-header">
          <div class="review-business-name">${escapeHtml(data.business_name)}</div>
          ${locationLine}
        </div>
        <p class="public-lead">Thank you for your visit! We'd love it if you'd take a moment to share your experience.</p>
        <div class="public-platform-links">
          ${data.platforms.map((p) => `
            <a href="${escapeHtml(p.url)}" target="_blank" rel="noreferrer" class="platform-link-btn">
              Leave a review on ${escapeHtml(p.name)}
            </a>
          `).join("")}
        </div>
      `;
    } catch (error) {
      container.innerHTML = `<p class="muted">${escapeHtml(error instanceof Error ? error.message : "Something went wrong.")}</p>`;
    }
  }

  type FeedbackQuestion = { id: string; text: string; type: "rating" | "text" };
  type FeedbackTokenData = { business_name: string; location_name: string; questions: FeedbackQuestion[] };

  function renderFeedbackQuestion(q: FeedbackQuestion): string {
    if (q.type === "rating") {
      const buttons = [1, 2, 3, 4, 5].map((n) => `
        <button type="button" class="rating-btn" data-qid="${escapeHtml(q.id)}" data-val="${n}">${n}</button>
      `).join("");
      return `
        <div class="feedback-question" data-question="${escapeHtml(q.id)}">
          <label class="feedback-question-label">${escapeHtml(q.text)}</label>
          <div class="rating-group" role="group" aria-label="${escapeHtml(q.text)}">
            ${buttons}
          </div>
          <input type="hidden" name="${escapeHtml(q.id)}" value="">
        </div>
      `;
    }
    return `
      <div class="feedback-question" data-question="${escapeHtml(q.id)}">
        <label class="feedback-question-label" for="fq-${escapeHtml(q.id)}">${escapeHtml(q.text)}</label>
        <textarea
          id="fq-${escapeHtml(q.id)}"
          name="${escapeHtml(q.id)}"
          class="feedback-textarea"
          rows="3"
          placeholder="Your answer (optional)"
        ></textarea>
      </div>
    `;
  }

  async function fetchFeedbackToken(apiBaseUrl: string, token: string) {
    const container = mountNode.querySelector<HTMLElement>("#public-content");
    if (!container) return;
    try {
      const resp = await fetch(`${apiBaseUrl}/v1/f/${encodeURIComponent(token)}`);
      if (!resp.ok) throw new Error("Feedback link not found or expired.");
      const data = await resp.json() as FeedbackTokenData;

      const locationLine = data.location_name
        ? `<div class="review-location">${escapeHtml(data.location_name)}</div>`
        : "";

      container.innerHTML = `
        <div class="review-business-header">
          <div class="review-business-name">${escapeHtml(data.business_name)}</div>
          ${locationLine}
        </div>
        <p class="public-lead feedback-intro">We are sorry we did not meet your needs. Thank you for helping us to improve our service.</p>
        <p class="feedback-scale-note">Please rate your service today: <strong>1</strong> – Far Below Expectation &nbsp;·&nbsp; <strong>5</strong> – Well Above Expectation</p>
        <form id="public-feedback-form" class="public-feedback-form">
          ${data.questions.map(renderFeedbackQuestion).join("")}
          <button class="primary-button feedback-submit-btn" type="submit">Submit feedback</button>
        </form>
        <div id="public-feedback-result"></div>
      `;
      bindPublicFeedbackForm(apiBaseUrl, token);
    } catch (error) {
      container.innerHTML = `<p class="muted">${escapeHtml(error instanceof Error ? error.message : "Something went wrong.")}</p>`;
    }
  }

  function bindPublicFeedbackForm(apiBaseUrl: string, token: string) {
    // Wire up rating buttons — clicking selects the value and updates the hidden input.
    mountNode.querySelectorAll<HTMLButtonElement>(".rating-btn").forEach((btn) => {
      btn.addEventListener("click", () => {
        const qid = btn.dataset.qid ?? "";
        const val = btn.dataset.val ?? "";
        const group = btn.closest(".feedback-question");
        if (!group) return;
        group.querySelectorAll<HTMLButtonElement>(".rating-btn").forEach((b) => b.classList.remove("selected"));
        btn.classList.add("selected");
        const hidden = group.querySelector<HTMLInputElement>(`input[name="${qid}"]`);
        if (hidden) hidden.value = val;
      });
    });

    mountNode.querySelector<HTMLFormElement>("#public-feedback-form")
      ?.addEventListener("submit", async (event) => {
        event.preventDefault();
        const form = event.currentTarget as HTMLFormElement;
        const formData = new FormData(form);
        const resultEl = mountNode.querySelector<HTMLElement>("#public-feedback-result");
        const btn = form.querySelector<HTMLButtonElement>(".feedback-submit-btn");

        const answers = Array.from(formData.entries())
          .filter(([, v]) => String(v).trim() !== "")
          .map(([k, v]) => ({ question_id: k, answer: String(v).trim() }));

        if (answers.length === 0) {
          if (resultEl) resultEl.innerHTML = `<p class="banner error">Please answer at least one question before submitting.</p>`;
          return;
        }

        if (btn) { btn.disabled = true; btn.textContent = "Sending…"; }

        try {
          const resp = await fetch(`${apiBaseUrl}/v1/f/${encodeURIComponent(token)}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ answers }),
          });
          if (!resp.ok) throw new Error("Submission failed.");
          form.style.display = "none";
          if (resultEl) resultEl.innerHTML = `<p class="public-lead">Thank you for your feedback! We appreciate you taking the time to help us improve.</p>`;
        } catch (error) {
          if (btn) { btn.disabled = false; btn.textContent = "Submit feedback"; }
          if (resultEl) resultEl.innerHTML = `<p class="banner error">${escapeHtml(error instanceof Error ? error.message : "An error occurred.")}</p>`;
        }
      });
  }

  getAuthSnapshot()
    .then(async (snapshot) => {
      console.log(`[auth] initial snapshot — user=${snapshot.user?.email ?? "none"}`);
      state.auth = snapshot;
      render();
      if (snapshot.user) {
        await refreshAppData();
      }
    })
    .catch((error) => {
      console.error("[auth] getAuthSnapshot failed:", error);
      setError(readError(error));
      render();
    });

  onAuthStateChange(async (snapshot) => {
    console.log(`[auth] state change — user=${snapshot.user?.email ?? "none"}`);
    state.auth = snapshot;
    setBusy(false);
    render();
    if (snapshot.user) {
      await refreshAppData();
    }
  });

  render();
}

function renderPublicShell(pathname: string) {
  const isReview = pathname.startsWith("/r/");
  return `
    ${renderDevEnvBar()}
    <div class="public-shell">
      <div class="public-hero">
        <div class="eyebrow">Rate Follow Up</div>
        <h2>${isReview ? "Leave a Review" : "Share Your Feedback"}</h2>
      </div>
      <div class="public-card" id="public-content">
        <p class="muted">Loading…</p>
      </div>
    </div>
  `;
}

function renderDevEnvBar(): string {
  const mode = getAppMode();
  if (mode !== "dev1" && mode !== "dev2") return "";
  return `<div class="dev-env-bar ${mode}">${mode.toUpperCase()} — development instance</div>`;
}

function resolveRoute(pathname: string): RouteName {
  if (pathname === "/profile-select") return "profile-select";
  if (pathname === "/test-accounts") return "test-accounts";
  if (pathname === "/team") return "team";
  const matched = routes.find((route) => route.path === pathname);
  return matched?.name ?? "dashboard";
}

function formatPhoneDisplay(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 11 && digits[0] === "1") {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phone;
}

function formatLogTimestamp(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${mm}/${dd}/${yyyy} ${hh}:${min}`;
}

function renderAuthLanding(state: {
  auth: AuthSnapshot;
  busy: boolean;
  notice: string;
  error: string;
}) {
  return `
    ${renderDevEnvBar()}
    <main class="auth-shell">
      <section class="hero-card">
        <div class="eyebrow">Rate Follow Up</div>
        <h1>Login or create an account</h1>
        <p class="lede">
          Continue with Google, or get a one-time sign-in link sent to your inbox — no password needed.
        </p>
        ${renderBanner(state)}
        ${state.auth.configured ? renderLoginPanel(state) : renderConfigPanel()}
      </section>
    </main>
  `;
}

function renderLoginPanel(state: { busy: boolean }) {
  return `
    <div class="stack">
      <button class="primary-button" data-google-login ${disabledAttr(state.busy)}>
        Continue with Google
      </button>
    </div>
  `;
}

function renderConfigPanel() {
  return `
    <div class="warning-card">
      <h2>Firebase configuration required</h2>
      <p>
        Add the <code>VITE_FIREBASE_*</code> environment variables
        before deploying or running the app.
      </p>
    </div>
  `;
}

function renderAuthedApp(state: {
  auth: AuthSnapshot;
  appData: AppData | null;
  route: RouteName;
  busy: boolean;
  loadingData: boolean;
  notice: string;
  error: string;
  csvResult: CsvValidationResult | null;
  csvFileName: string;
  lastSavedUploadId: string;
  selectedUploadDetails: UploadDetails | null;
  uploadAttested: boolean;
  uploadMode: "file" | "manual";
  billingShowUpdateForm: boolean;
  pendingPlatforms: ReviewPlatform[];
  sendStatus: "idle" | "sending" | "complete" | "failed" | "stopped";
  sendResult: SendResult | null;
  selectedProfileId: string | null;
  phoneLog: PhoneLogEntry[] | null;
  phoneLogLoading: boolean;
  logPhoneFilter: string;
  numberHistory: NumberHistoryEntry[] | null;
  numberHistoryLoading: boolean;
  numberHistoryFilter: string;
  isTesting: boolean;
  activeAccountIsTesting: boolean;
  testingAccounts: TestingAccount[] | null;
  testingAccountsLoading: boolean;
  authorizedUsers: AccountAccess[] | null;
  authorizedUsersLoading: boolean;
  linkedAccounts: AccountAccess[] | null;
  guestAccountId: string | null;
  guestOwnerEmail: string | null;
}) {
  const profile = state.appData?.profile;
  const profileLabel = profile
    ? escapeHtml(profile.location_name || profile.business_name || "Location")
    : "";

  return `
    ${renderDevEnvBar()}
    <div class="app-shell">
      <aside class="sidebar">
        <div class="sidebar-top">
          <div class="sidebar-brand-row">
            <div class="sidebar-brand">Rate Follow Up</div>
            ${state.appData?.account != null ? `
              <div class="sidebar-token-badge" title="Token balance">
                ${formatNumber(state.appData.account.token_balance)} <span class="sidebar-token-label">tokens</span>
              </div>
            ` : ""}
          </div>
          <div class="account-label">${escapeHtml(state.auth.user?.email ?? "")}</div>
          ${profileLabel ? `
            <div class="profile-label">${profileLabel}</div>
            <button class="sidebar-switch-btn" data-switch-profile ${disabledAttr(state.busy)}>
              Switch location
            </button>
          ` : ""}
          <button class="sidebar-signout-btn" data-sign-out>
            Sign out
          </button>
        </div>
        <nav class="nav-list">
          ${routes
            .filter((route) => {
              if (route.name === "team" && state.guestAccountId) return false;
              return true;
            })
            .map(
              (route) => `
                <a
                  href="${route.path}"
                  data-nav
                  class="nav-link ${route.name === state.route ? "active" : ""}"
                >
                  ${route.label}
                </a>
              `,
            )
            .join("")}
          ${state.isTesting ? `
            <a
              href="/test-accounts"
              data-nav
              class="nav-link nav-link-testing ${state.route === "test-accounts" ? "active" : ""}"
            >
              Test accounts
            </a>
          ` : ""}
        </nav>
        ${state.guestAccountId ? `
          <div class="guest-account-banner">
            <div class="guest-account-label">Viewing as</div>
            <div class="guest-account-email">${escapeHtml(state.guestOwnerEmail ?? "")}</div>
            <button class="secondary-button small-button" data-switch-own-account>Switch back</button>
          </div>
        ` : (state.linkedAccounts && state.linkedAccounts.length > 0) ? `
          <div class="linked-accounts-section">
            <div class="linked-accounts-label">Linked accounts</div>
            ${state.linkedAccounts.map((a, i) => `
              <button class="linked-account-btn" data-switch-guest-account="${i}">
                ${escapeHtml(a.owner_email)}
              </button>
            `).join("")}
          </div>
        ` : ""}
      </aside>
      <main class="content-shell">
        ${renderBanner(state)}
        ${state.loadingData ? renderLoadingPanel() : renderRouteContent({
          appData: state.appData,
          route: state.route,
          csvResult: state.csvResult,
          csvFileName: state.csvFileName,
          lastSavedUploadId: state.lastSavedUploadId,
          selectedUploadDetails: state.selectedUploadDetails,
          uploadAttested: state.uploadAttested,
          uploadMode: state.uploadMode,
          billingShowUpdateForm: state.billingShowUpdateForm,
          pendingPlatforms: state.pendingPlatforms,
          sendStatus: state.sendStatus,
          sendResult: state.sendResult,
          phoneLog: state.phoneLog,
          phoneLogLoading: state.phoneLogLoading,
          logPhoneFilter: state.logPhoneFilter,
          numberHistory: state.numberHistory,
          numberHistoryLoading: state.numberHistoryLoading,
          numberHistoryFilter: state.numberHistoryFilter,
          isTesting: state.isTesting,
          activeAccountIsTesting: state.activeAccountIsTesting,
          testingAccounts: state.testingAccounts,
          testingAccountsLoading: state.testingAccountsLoading,
          authorizedUsers: state.authorizedUsers,
          authorizedUsersLoading: state.authorizedUsersLoading,
          guestAccountId: state.guestAccountId,
          busy: state.busy,
        })}
      </main>
    </div>
  `;
}

function renderRouteContent(state: {
  appData: AppData | null;
  route: RouteName;
  csvResult: CsvValidationResult | null;
  csvFileName: string;
  lastSavedUploadId: string;
  selectedUploadDetails: UploadDetails | null;
  uploadAttested: boolean;
  uploadMode: "file" | "manual";
  billingShowUpdateForm: boolean;
  pendingPlatforms: ReviewPlatform[];
  sendStatus: "idle" | "sending" | "complete" | "failed" | "stopped";
  sendResult: SendResult | null;
  phoneLog: PhoneLogEntry[] | null;
  phoneLogLoading: boolean;
  logPhoneFilter: string;
  numberHistory: NumberHistoryEntry[] | null;
  numberHistoryLoading: boolean;
  numberHistoryFilter: string;
  isTesting: boolean;
  activeAccountIsTesting: boolean;
  testingAccounts: TestingAccount[] | null;
  testingAccountsLoading: boolean;
  authorizedUsers: AccountAccess[] | null;
  authorizedUsersLoading: boolean;
  guestAccountId: string | null;
  busy: boolean;
}) {
  switch (state.route) {
    case "profile-select":
      return renderProfileSelectPage(state.appData);
    case "profile":
      return renderProfilePage(state.appData, state.pendingPlatforms, state.isTesting);
    case "upload":
      return renderUploadPage(
        state.appData,
        state.csvResult,
        state.csvFileName,
        state.lastSavedUploadId,
        state.selectedUploadDetails,
        state.uploadAttested,
        state.uploadMode,
        state.sendStatus,
        state.sendResult,
        state.activeAccountIsTesting,
      );
    case "billing":
      return renderBillingPage(
        state.appData,
        state.billingShowUpdateForm,
        Boolean(state.guestAccountId),
        state.activeAccountIsTesting,
      );
    case "log":
      return renderLogPage(state.appData, state.phoneLog, state.phoneLogLoading, state.logPhoneFilter);
    case "numbers":
      return renderNumberHistoryPage(state.appData, state.numberHistory, state.numberHistoryLoading, state.numberHistoryFilter);
    case "team":
      return state.guestAccountId
        ? renderWelcomePage(state.appData, state.sendStatus, state.sendResult, state.activeAccountIsTesting)
        : renderTeamPage(state.authorizedUsers, state.authorizedUsersLoading, state.busy);
    case "test-accounts":
      return state.isTesting
        ? renderTestAccountsPage(state.testingAccounts, state.testingAccountsLoading)
        : renderWelcomePage(state.appData, state.sendStatus, state.sendResult, state.activeAccountIsTesting);
    case "dashboard":
    default:
      return renderWelcomePage(state.appData, state.sendStatus, state.sendResult, state.activeAccountIsTesting);
  }
}

type SendResult = { total: number; enqueued: number; skipped: number; failed: number; results: Array<{ phone: string; status: string; reason?: string }> };

function formatSendResultStatus(status: string): string {
  switch (status) {
    case "enqueued": return "Queued for sending";
    case "sent": return "Sent";
    case "failed": return "Failed to queue";
    case "skipped_opted_out": return "Skipped — opted out";
    case "skipped_recent": return "Skipped — contacted within 1 year";
    case "no_phone": return "Skipped — no valid number";
    default: return status;
  }
}

function renderSendResultsDropdown(sendResult: SendResult) {
  if (!sendResult.results || sendResult.results.length === 0) return "";

  const rows = sendResult.results.map((r) => {
    const isSent = r.status === "enqueued" || r.status === "sent";
    const isFailure = r.status === "failed";
    const label = formatSendResultStatus(r.status);
    const reasonHtml = r.reason
      ? `<div class="send-result-reason">${escapeHtml(r.reason)}</div>`
      : "";
    return `
      <div class="send-result-row ${isSent ? "result-sent" : isFailure ? "result-failed" : "result-skipped"}">
        <div class="send-result-main">
          <span class="send-result-phone">${escapeHtml(r.phone || "Unknown")}</span>
          <span class="send-result-label">${label}</span>
        </div>
        ${reasonHtml}
      </div>
    `;
  }).join("");

  const summary = sendResult.failed > 0
    ? `${sendResult.enqueued} queued, ${sendResult.failed} failed — see details`
    : `All ${sendResult.enqueued} queued for sending`;

  return `
    <details class="send-results-details">
      <summary class="send-results-summary">${summary}</summary>
      <div class="send-results-list">${rows}</div>
    </details>
  `;
}

function renderSendStatusBadge(
  sendStatus: "idle" | "sending" | "complete" | "failed" | "stopped",
  sendResult: SendResult | null,
) {
  if (sendStatus === "idle") return "";
  if (sendStatus === "sending") {
    return `<div class="send-status-badge status-sending">⏳ Queuing messages…</div>`;
  }
  if (sendStatus === "complete" && sendResult) {
    const msg = sendResult.failed > 0
      ? `✓ ${sendResult.enqueued} queued, ${sendResult.failed} failed`
      : `✓ ${sendResult.enqueued} message${sendResult.enqueued === 1 ? "" : "s"} queued for sending`;
    return `
      <div class="send-status-badge status-complete">${msg}</div>
      <div class="send-status-note">Messages send in the background — this batch updates as they go out.</div>
      ${renderSendResultsDropdown(sendResult)}
    `;
  }
  if (sendStatus === "failed") {
    return `<div class="send-status-badge status-failed">✗ Could not queue — check error above</div>`;
  }
  if (sendStatus === "stopped") {
    return `<div class="send-status-badge status-stopped">⊘ Send cancelled</div>`;
  }
  return "";
}

function renderLogPage(
  appData: AppData | null,
  phoneLog: PhoneLogEntry[] | null,
  loading: boolean,
  phoneFilter = "",
): string {
  const profile = appData?.profile;
  if (!profile) {
    return `<div class="page-section"><p class="muted">Select a location to view its activity log.</p></div>`;
  }

  if (loading) {
    return `<div class="page-section"><p class="muted">Loading activity log…</p></div>`;
  }

  if (!phoneLog) {
    return `<div class="page-section"><p class="muted">Navigate to this page to load the activity log.</p></div>`;
  }

  if (phoneLog.length === 0) {
    return `
      <div class="page-section">
        <h2 class="page-title">Activity Log</h2>
        <p class="muted">No activity yet for ${escapeHtml(profile.location_name || profile.business_name)}.</p>
      </div>
    `;
  }

  const eventLabel: Record<string, string> = {
    loaded: "Loaded into the system",
    rejected: "Rejected",
    suppressed: "Suppressed",
    sent: "Sent opt-in message",
    send_failed: "Send failed",
    skipped: "Skipped",
    recv_yes: "Replied YES",
    recv_no: "Replied NO",
    opted_out: "Opted out",
    clicked_review: "Clicked review link",
    feedback: "Submitted feedback",
  };

  const eventClass: Record<string, string> = {
    loaded: "log-event-loaded",
    rejected: "log-event-rejected",
    suppressed: "log-event-suppressed",
    sent: "log-event-sent",
    send_failed: "log-event-failed",
    skipped: "log-event-skipped",
    recv_yes: "log-event-yes",
    recv_no: "log-event-no",
    opted_out: "log-event-opted-out",
    clicked_review: "log-event-yes",
    feedback: "log-event-yes",
  };

  // Build the per-number filter options: one entry per distinct number,
  // grouped by digits so the same number in different formats collapses to one.
  const digitsKey = (phone: string) => phone.replace(/\D/g, "");
  const numbers = new Map<string, { display: string; count: number }>();
  for (const entry of phoneLog) {
    const key = digitsKey(entry.phone);
    if (!key) continue;
    const existing = numbers.get(key);
    if (existing) existing.count += 1;
    else numbers.set(key, { display: formatPhoneDisplay(entry.phone), count: 1 });
  }
  const sortedNumbers = [...numbers.entries()].sort((a, b) => a[1].display.localeCompare(b[1].display));

  // Apply the active filter (client-side; the full log is already loaded).
  const activeFilter = numbers.has(phoneFilter) ? phoneFilter : "";
  const visible = activeFilter ? phoneLog.filter((e) => digitsKey(e.phone) === activeFilter) : phoneLog;

  const options = [
    `<option value=""${activeFilter === "" ? " selected" : ""}>All numbers (${numbers.size})</option>`,
    ...sortedNumbers.map(
      ([key, { display, count }]) =>
        `<option value="${escapeHtml(key)}"${key === activeFilter ? " selected" : ""}>${escapeHtml(display)} (${count})</option>`,
    ),
  ].join("");

  const rows = visible.map((entry) => {
    const ts = formatLogTimestamp(entry.timestamp);
    const phone = formatPhoneDisplay(entry.phone);
    const label = eventLabel[entry.event] ?? entry.event;
    const cls = eventClass[entry.event] ?? "";
    return `
      <tr class="log-row ${cls}">
        <td class="log-ts">${escapeHtml(ts)}</td>
        <td class="log-phone">${escapeHtml(phone)}</td>
        <td class="log-detail">${escapeHtml(entry.detail || label)}</td>
      </tr>
    `;
  }).join("");

  const countLabel = activeFilter
    ? `${visible.length} event${visible.length !== 1 ? "s" : ""} for ${escapeHtml(numbers.get(activeFilter)!.display)} — newest first`
    : `${phoneLog.length} event${phoneLog.length !== 1 ? "s" : ""} — newest first`;

  return `
    <div class="page-section">
      <h2 class="page-title">Activity Log</h2>
      <div class="log-toolbar">
        <label class="log-filter">
          <span class="log-filter-label">Number</span>
          <select class="text-input" data-log-phone-filter>${options}</select>
        </label>
        <p class="muted log-count">${countLabel}</p>
      </div>
      <div class="log-table-wrap">
        <table class="log-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>Phone</th>
              <th>Event</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
    </div>
  `;
}

// canonicalConvState maps legacy / row-style state values (from pre-#34
// conversation docs) onto the current state-machine states so the label
// functions have a single vocabulary to switch on.
function canonicalConvState(state: string): string {
  switch (state) {
    case "queued": return "waiting_opt_in_1";
    case "optin_1_sent": return "waiting_opt_in_2";
    case "optin_sent":
    case "sent": return "waiting_response";
    case "completed_yes": return "sent_review_link";
    case "completed_no": return "sent_feedback_link";
    case "failed": return "send_error";
    default: return state;
  }
}

// numberInteraction renders the ordered "what happened" summary for one number.
// The conversation flow is linear, so the current state implies every prior step.
function numberInteraction(entry: NumberHistoryEntry): string {
  if (entry.state) {
    const optin = "Opt-in 1 sent; Opt-in 2 sent";
    switch (canonicalConvState(entry.state)) {
      case "waiting_opt_in_1": return "Queued — not yet sent";
      case "waiting_opt_in_2": return "Opt-in 1 sent";
      case "waiting_response": return `${optin}; awaiting reply`;
      case "sending_review_link": return `${optin}; Replied YES`;
      case "sent_review_link": return `${optin}; Replied YES; Review link sent`;
      case "clicked_review": return `${optin}; Replied YES; Review link sent; Review link clicked`;
      case "sending_feedback_link": return `${optin}; Replied NO`;
      case "sent_feedback_link": return `${optin}; Replied NO; Feedback form sent`;
      case "clicked_form": return `${optin}; Replied NO; Feedback form sent; Form clicked`;
      case "form_submitted": return `${optin}; Replied NO; Feedback form sent; Form clicked; Form submitted`;
      case "opted_out": return "Opted out (replied STOP)";
      case "send_error": return "Send error — message failed";
      default: return entry.state;
    }
  }
  switch (entry.rowStatus) {
    case "rejected_invalid":
    case "no_phone": return "Did not send — invalid number";
    case "rejected_duplicate": return "Did not send — duplicate number";
    case "rejected_suppressed":
    case "skipped_recent": return "Did not send — contacted within the last year";
    case "skipped_opted_out": return "Did not send — previously opted out";
    case "queued":
    case "valid": return "Loaded — not yet sent";
    default: return entry.rowStatus ? `Loaded — ${entry.rowStatus}` : "Loaded";
  }
}

// numberOutcome is the short at-a-glance status chip.
function numberOutcome(entry: NumberHistoryEntry): { label: string; cls: string } {
  if (entry.state) {
    switch (canonicalConvState(entry.state)) {
      case "clicked_review":
      case "form_submitted": return { label: "Completed", cls: "nh-good" };
      case "clicked_form": return { label: "Opened form", cls: "nh-warn" };
      case "sent_review_link": return { label: "Review link sent", cls: "nh-warn" };
      case "sent_feedback_link": return { label: "Feedback form sent", cls: "nh-warn" };
      case "sending_review_link":
      case "sending_feedback_link": return { label: "Sending link", cls: "nh-warn" };
      case "waiting_response": return { label: "Awaiting reply", cls: "nh-warn" };
      case "waiting_opt_in_1":
      case "waiting_opt_in_2": return { label: "Sending opt-in", cls: "nh-neutral" };
      case "opted_out": return { label: "Opted out", cls: "nh-skip" };
      case "send_error": return { label: "Send failed", cls: "nh-bad" };
      default: return { label: entry.state, cls: "nh-neutral" };
    }
  }
  switch (entry.rowStatus) {
    case "rejected_invalid":
    case "no_phone": return { label: "Invalid — not sent", cls: "nh-bad" };
    case "rejected_duplicate": return { label: "Duplicate — not sent", cls: "nh-skip" };
    case "rejected_suppressed":
    case "skipped_recent": return { label: "Skipped — recent", cls: "nh-skip" };
    case "skipped_opted_out": return { label: "Opted out", cls: "nh-skip" };
    case "queued":
    case "valid": return { label: "Not yet sent", cls: "nh-neutral" };
    default: return { label: entry.rowStatus ?? "—", cls: "nh-neutral" };
  }
}

function renderNumberHistoryPage(
  appData: AppData | null,
  history: NumberHistoryEntry[] | null,
  loading: boolean,
  filter = "",
): string {
  const profile = appData?.profile;
  if (!profile) {
    return `<div class="page-section"><p class="muted">Select a location to view its number history.</p></div>`;
  }
  if (loading) {
    return `<div class="page-section"><p class="muted">Loading number history…</p></div>`;
  }
  if (!history) {
    return `<div class="page-section"><p class="muted">Navigate to this page to load the number history.</p></div>`;
  }
  if (history.length === 0) {
    return `
      <div class="page-section">
        <h2 class="page-title">Number History</h2>
        <p class="muted">No numbers yet for ${escapeHtml(profile.location_name || profile.business_name)}.</p>
      </div>
    `;
  }

  const q = filter.trim().replace(/\D/g, "");
  const visible = q ? history.filter((e) => e.phone.includes(q)) : history;

  const rows = visible.map((entry) => {
    const outcome = numberOutcome(entry);
    const batchBadge = entry.batchCount > 1 ? `<span class="nh-batches" title="Appears in ${entry.batchCount} batches">×${entry.batchCount}</span>` : "";
    return `
      <tr>
        <td class="nh-number">
          <button type="button" class="nh-number-link" data-view-number="${escapeHtml(entry.phone)}" title="View this number in the Activity Log">${escapeHtml(formatPhoneDisplay(entry.display))}</button>
          ${batchBadge}
        </td>
        <td class="nh-date">${entry.dateLoaded ? escapeHtml(formatLogTimestamp(entry.dateLoaded)) : "—"}</td>
        <td class="nh-date">${entry.dateSent ? escapeHtml(formatLogTimestamp(entry.dateSent)) : "—"}</td>
        <td class="nh-interaction">${escapeHtml(numberInteraction(entry))}</td>
        <td><span class="nh-chip ${outcome.cls}">${escapeHtml(outcome.label)}</span></td>
      </tr>
    `;
  }).join("");

  const countLabel = q
    ? `${visible.length} of ${history.length} number${history.length !== 1 ? "s" : ""}`
    : `${history.length} number${history.length !== 1 ? "s" : ""} — most recent activity first`;

  return `
    <div class="page-section">
      <h2 class="page-title">Number History</h2>
      <div class="log-toolbar">
        <label class="log-filter">
          <span class="log-filter-label">Search</span>
          <input type="tel" class="text-input" data-number-search placeholder="Type a number…" value="${escapeHtml(filter)}" />
        </label>
        <p class="muted log-count">${countLabel}</p>
      </div>
      <div class="log-table-wrap">
        <table class="log-table nh-table">
          <thead>
            <tr>
              <th>Number</th>
              <th>Loaded</th>
              <th>Sent</th>
              <th>Interaction</th>
              <th>Outcome</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
    </div>
  `;
}

function renderWelcomePage(
  appData: AppData | null,
  sendStatus: "idle" | "sending" | "complete" | "failed" | "stopped" = "idle",
  sendResult: SendResult | null = null,
  isTesting = false,
) {
  const account = appData?.account;
  const profile = appData?.profile;
  const uploads = appData?.uploads ?? [];

  const profileDone = Boolean(profile?.is_profile_complete);
  const uploadDone = uploads.length > 0;
  const billingDone = isTesting ? true : Boolean(account?.square_card_id);
  const readyToSendCount = uploads
    .filter((u) => u.status === "validated")
    .reduce((sum, u) => sum + u.valid_rows, 0) +
    uploads.filter((u) => u.status === "ready")
    .reduce((sum, u) => sum + u.queued_rows, 0);
  const totalQueued = uploads.reduce((sum, u) => sum + u.queued_rows, 0);

  const profileChecks = buildProfileChecks(profile);

  const step1 = profileDone
    ? renderStepDone(1, "Profile complete")
    : renderStepActive(1, "Complete your profile",
        `<p>Fill in your business details so Rate Follow Up knows where to send review replies.</p>
         ${renderMiniChecklist(profileChecks)}
         <a href="/profile" data-nav class="primary-button step-cta">Complete profile →</a>`,
      );

  const billingStatus = account?.billing_status ?? "not_configured";
  const billingWarning = billingStatus === "past_due" || billingStatus === "canceled";

  const step2 = isTesting
    ? renderStepDone(2, "Billing — using test tokens")
    : !profileDone
    ? renderStepUpcoming(2, "Set up billing")
    : billingWarning
    ? renderStepWarning(2, "Billing issue",
        `<p>There's a problem with your payment method: <strong>${formatBillingStatus(billingStatus)}</strong>. Messages cannot be sent until this is resolved.</p>
         <a href="/billing" data-nav class="primary-button step-cta">Fix billing →</a>`,
      )
    : billingDone
    ? renderStepDone(2, "Billing set up")
    : renderStepActive(2, "Set up billing",
        `<p>Add a payment method to activate your plan and start sending messages.</p>
         <a href="/billing" data-nav class="primary-button step-cta">Set up billing →</a>`,
      );

  const step3 = !profileDone || !billingDone
    ? renderStepUpcoming(3, "Upload phone numbers")
    : uploadDone
    ? renderStepDone(3, `${uploads.length} batch${uploads.length === 1 ? "" : "es"} uploaded`)
    : renderStepActive(3, "Upload phone numbers",
        `<p>Upload a CSV, Excel, or text file of customer phone numbers to get started.</p>
         <a href="/upload" data-nav class="primary-button step-cta">Upload numbers →</a>`,
      );

  const step4 = !profileDone || !uploadDone || !billingDone
    ? renderStepUpcoming(4, "Send messages")
    : readyToSendCount > 0
    ? renderStepActive(4, "Send review requests",
        `<section class="grid compact-grid" style="margin-bottom:4px">
           ${renderMetric("Ready to send", formatNumber(readyToSendCount))}
           ${sendResult && sendResult.enqueued > 0 ? renderMetric("Queued for sending", formatNumber(sendResult.enqueued)) : ""}
         </section>
         <p>Resend suppression is applied automatically before numbers are queued.</p>
         ${renderSendStatusBadge(sendStatus, sendResult)}
         <div class="welcome-actions">
           <button class="primary-button step-cta" data-send-all ${sendStatus === "sending" ? "disabled" : ""}>${sendStatus === "sending" ? "Starting…" : "Start sending"}</button>
           ${sendStatus === "sending" ? `<button class="secondary-button welcome-action" data-stop-send>Stop</button>` : `<a href="/upload" data-nav class="secondary-button welcome-action">Review batches</a>`}
         </div>`,
      )
    : renderStepDone(4, `${formatNumber(totalQueued)} message${totalQueued === 1 ? "" : "s"} queued`);

  const activitySummary = uploadDone && profileDone
    ? renderActivitySummary(account, uploads, totalQueued, appData?.analytics ?? null)
    : "";

  return `
    <div class="dashboard-hero">
      <h1 class="dashboard-welcome">Welcome to Rate Follow Up</h1>
      <div class="dashboard-divider"></div>
      <h2 class="dashboard-title">Dashboard</h2>
    </div>
    <div class="onboarding-flow">
      ${step1}
      ${step2}
      ${step3}
      ${step4}
    </div>
    ${activitySummary}
  `;
}

function buildProfileChecks(profile: BusinessProfile | null | undefined) {
  return [
    { label: "Business name", done: Boolean(profile?.business_name?.trim()) },
    { label: "Business address", done: Boolean(profile?.business_address?.trim()) },
    { label: "Business phone", done: Boolean(profile?.business_phone?.trim()) },
    { label: "Contact name", done: Boolean(profile?.contact_name?.trim()) },
    { label: "Contact email", done: Boolean(profile?.primary_email?.trim()) },
    {
      label: "At least one review platform URL",
      done: Boolean(profile?.review_platforms?.some((p) => p.enabled && p.url.trim())),
    },
  ];
}

function renderMiniChecklist(checks: Array<{ label: string; done: boolean }>) {
  return `
    <ul class="profile-checklist">
      ${checks
        .map(
          (c) => `
            <li class="checklist-item ${c.done ? "done" : "todo"}">
              <span class="check-icon">${c.done ? "✓" : "✗"}</span>
              ${escapeHtml(c.label)}
            </li>
          `,
        )
        .join("")}
    </ul>
  `;
}

function renderStepActive(num: number, title: string, body: string) {
  return `
    <div class="step-card step-active">
      <div class="step-header">
        <span class="step-badge badge-active">${num}</span>
        <h3 class="step-title">${escapeHtml(title)}</h3>
      </div>
      <div class="step-body">${body}</div>
    </div>
  `;
}

function renderStepWarning(num: number, title: string, body: string) {
  return `
    <div class="step-card step-warning">
      <div class="step-header">
        <span class="step-badge badge-warning">!</span>
        <h3 class="step-title">${escapeHtml(title)}</h3>
      </div>
      <div class="step-body">${body}</div>
    </div>
  `;
}

function renderStepDone(num: number, label: string) {
  return `
    <div class="step-card step-done">
      <div class="step-header">
        <span class="step-badge badge-done">✓</span>
        <span class="step-done-label">${num}. ${escapeHtml(label)}</span>
      </div>
    </div>
  `;
}

function renderStepUpcoming(num: number, label: string) {
  return `
    <div class="step-card step-upcoming">
      <div class="step-header">
        <span class="step-badge badge-upcoming">${num}</span>
        <span class="step-upcoming-label">${escapeHtml(label)}</span>
      </div>
    </div>
  `;
}

function renderActivitySummary(
  account: BusinessAccount | null | undefined,
  uploads: UploadRecord[],
  totalQueued: number,
  analytics: ProfileAnalytics | null,
) {
  const selectedPlan = getUsagePlan(account?.usage_plan_id);
  return `
    <section class="grid" style="margin-top:16px">
      ${renderMetric("Plan", selectedPlan.label)}
      ${renderMetric("Uploads", String(uploads.length))}
      ${renderMetric("Queued / sent", formatNumber(totalQueued))}
    </section>
    ${analytics ? renderAnalyticsSection(analytics) : ""}
  `;
}

function renderAnalyticsSection(a: ProfileAnalytics) {
  return `
    <section class="analytics-section" style="margin-top:24px">
      <h3 class="section-heading">Data</h3>
      <div class="grid">
        ${renderMetric("In process", formatNumber(a.in_process))}
        ${renderMetric("Replied yes", formatNumber(a.replied_yes))}
        ${renderMetric("Replied no", formatNumber(a.replied_no))}
        ${renderMetric("Opted out", formatNumber(a.opted_out))}
        ${renderMetric("Clicked review link", formatNumber(a.clicked_review))}
        ${renderMetric("Clicked feedback form", formatNumber(a.clicked_form))}
        ${renderMetric("Submitted feedback", formatNumber(a.submitted_feedback))}
        ${renderMetric("Skipped (suppressed)", formatNumber(a.skipped))}
        ${renderMetric("Total contacts", formatNumber(a.total))}
      </div>
    </section>
  `;
}

function renderPlatformEditorRows(platforms: ReviewPlatform[]) {
  if (platforms.length === 0) {
    return `<p class="muted">No review platforms added yet.</p>`;
  }
  return platforms.map((p, i) => `
    <div class="platform-row">
      <label class="platform-toggle" title="${p.enabled ? "Enabled" : "Disabled"}">
        <input type="checkbox" data-toggle-platform="${i}" ${p.enabled ? "checked" : ""} />
      </label>
      ${p.id === "google" || p.id === "facebook"
        ? `<span class="platform-name-fixed">${escapeHtml(p.name)}</span>`
        : `<input class="platform-name-input" type="text" placeholder="Platform name" value="${escapeHtml(p.name)}" data-platform-name="${i}" />`
      }
      <input
        class="platform-url-input"
        type="url"
        placeholder="${p.id === "google" ? "https://g.page/r/..." : p.id === "facebook" ? "https://facebook.com/YOURPAGE/reviews" : "https://"}"
        value="${escapeHtml(p.url)}"
        data-platform-url="${i}"
      />
      ${p.id !== "google" && p.id !== "facebook"
        ? `<button type="button" class="platform-remove-btn" data-remove-platform="${i}" title="Remove">✕</button>`
        : ""
      }
    </div>
  `).join("");
}

function renderProfilePage(appData: AppData | null, platforms: ReviewPlatform[], isTesting = false) {
  const profile = appData?.profile;
  const account = appData?.account;

  const req = (filled: boolean, key: string) =>
    `<span class="field-status-badge ${filled ? "badge-done" : "badge-needed"}" data-field-badge="${key}">${filled ? "✓" : "✗"}</span>`;

  const hasReviewPlatform = platforms.some((p) => p.enabled && p.url.trim());

  return `
    <h2 class="page-heading">Business profile</h2>
    <section class="panel">
      <form class="profile-form-grid" data-profile-form>
        <label class="field">
          <span>Location name</span>
          <input
            name="location_name"
            value="${escapeHtml(profile?.location_name ?? "")}"
            placeholder="e.g. Downtown, North Location"
          />
        </label>
        <label class="field">
          <span>Business name ${req(Boolean(profile?.business_name?.trim()), "business_name")}</span>
          <input
            name="business_name"
            value="${escapeHtml(profile?.business_name ?? "")}"
          />
        </label>
        <label class="field field-span-2">
          <span>Business address ${req(Boolean(profile?.business_address?.trim()), "business_address")}</span>
          <textarea
            name="business_address"
            rows="3"
          >${escapeHtml(profile?.business_address ?? "")}</textarea>
        </label>
        <label class="field">
          <span>Business phone ${req(Boolean(profile?.business_phone?.trim()), "business_phone")}</span>
          <input
            name="business_phone"
            type="tel"
            value="${escapeHtml(profile?.business_phone ?? "")}"
          />
        </label>
        <label class="field">
          <span>Your name ${req(Boolean(profile?.contact_name?.trim()), "contact_name")}</span>
          <input
            name="contact_name"
            value="${escapeHtml(profile?.contact_name ?? "")}"
          />
        </label>
        <label class="field">
          <span>Your email ${req(Boolean(profile?.primary_email?.trim()), "primary_email")}</span>
          <input
            name="primary_email"
            type="email"
            value="${escapeHtml(profile?.primary_email ?? "")}"
          />
        </label>
        <div class="field field-span-2">
          <span class="field-label">
            Review platforms ${req(hasReviewPlatform, "review_platforms")}
          </span>
          <small class="field-help">
            Customers who reply YES get a link to this page. Add the URLs where you want them to leave reviews.
            At least one platform must be enabled with a URL.
          </small>
          <div class="platform-editor" data-platform-editor>
            ${renderPlatformEditorRows(platforms)}
          </div>
          <button type="button" class="platform-add-btn" data-add-platform>+ Add platform</button>
        </div>
        <details class="help-disclosure">
          <summary>How to get your Google review link</summary>
          <div class="help-disclosure-body">
            <ol>
              <li>Open your Google Business Profile at <a href="https://business.google.com/" target="_blank" rel="noreferrer">business.google.com</a>.</li>
              <li>Select <strong>Read Reviews</strong>, then choose <strong>Get more reviews</strong>.</li>
              <li>Copy your review link and paste it into the Google URL field above.</li>
            </ol>
          </div>
        </details>
        <button class="primary-button profile-save-btn" type="button" data-save-profile>Save profile</button>
      </form>
    </section>
    ${isTesting ? `
    <details class="danger-zone">
      <summary>Testing: reset data</summary>
      <div class="danger-zone-body">
        <p><strong>Refill tokens (+250)</strong> — adds 250 tokens without going through billing. Use when tokens were drained by failed test sends.</p>
        <button class="secondary-button" data-refill-tokens>Add 250 tokens</button>
        <hr style="margin:16px 0;border-color:#fca5a5" />
        <p><strong>Clear phone numbers</strong> — removes recent-send and opt-out blockers for this location so test numbers can be used again. Enter one or more numbers separated by commas, spaces, or new lines. Activity log entries stay in place.</p>
        <form class="inline-form" onsubmit="return false">
          <textarea
            name="test_phone_number"
            class="text-input"
            rows="2"
            placeholder="(601) 555-1234, (601) 555-5678"
          ></textarea>
          <button type="button" class="secondary-button" data-clear-phone-blockers>Clear phone numbers</button>
        </form>
        <hr style="margin:16px 0;border-color:#fca5a5" />
        <p><strong>Clear batches &amp; phone history</strong> — deletes all upload batches and suppression history. Profile and billing stay intact. Use this to re-test with numbers that have already been messaged.</p>
        <button class="danger-button" data-clear-batches>Clear all batches &amp; history</button>
        <hr style="margin:16px 0;border-color:#fca5a5" />
        <p><strong>Delete account</strong> — permanently deletes all Firestore data and removes your login.</p>
        <button class="danger-button" data-delete-account>Delete my account data</button>
      </div>
    </details>
    ` : ""}
  `;
}

function renderProfileSelectPage(appData: AppData | null) {
  const profiles = appData?.profiles ?? [];

  const locationRows = profiles.map((p) => `
    <li class="location-item" data-select-profile="${escapeHtml(p.id)}">
      <div class="location-name">${escapeHtml(p.location_name || p.business_name || "Unnamed location")}</div>
      ${p.business_address ? `<div class="location-address">${escapeHtml(p.business_address)}</div>` : `<div class="location-address location-address-empty">No address on file</div>`}
    </li>
  `).join("");

  return `
    <div class="page-hero">
      <div class="eyebrow">Rate Follow Up</div>
      <h2 class="page-title">Your locations</h2>
    </div>
    ${profiles.length > 0 ? `<ul class="location-list">${locationRows}</ul>` : ""}
    <section class="create-location-section">
      <form class="stack-panel" data-create-profile-form>
        <label class="field">
          <span>Location name</span>
          <input
            name="location_name"
            placeholder="e.g. Downtown, North Location, Main Street"
            required
          />
        </label>
        <button class="primary-button create-location-btn" type="submit">Create location</button>
      </form>
    </section>
  `;
}

function renderTeamPage(
  authorizedUsers: AccountAccess[] | null,
  loading: boolean,
  busy = false,
) {
  const rows = loading
    ? `<p class="muted">Loading…</p>`
    : authorizedUsers === null
    ? `<p class="muted">Not loaded.</p>`
    : authorizedUsers.length === 0
    ? `<p class="muted">No team members yet. Add someone below.</p>`
    : `
      <div class="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Email</th>
              <th>Added by</th>
              <th>Added</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            ${authorizedUsers.map((u) => `
              <tr>
                <td>${escapeHtml(u.authorized_email)}</td>
                <td class="muted-sm">${escapeHtml(u.added_by)}</td>
                <td class="muted-sm">${formatDate(u.created_at)}</td>
                <td>
                  <button
                    class="secondary-button small-button"
                    data-remove-authorized-user="${escapeHtml(u.authorized_email)}"
                  >Remove</button>
                </td>
              </tr>
            `).join("")}
          </tbody>
        </table>
      </div>
    `;

  return `
    <h2 class="page-heading">Team</h2>
    <section class="panel stack-panel">
      <h3>Authorized users</h3>
      <p class="muted-sm">Users you add here can sign in with their own account and access your locations, upload numbers, and send messages using your billing. They cannot change billing or payment details.</p>
      ${rows}
    </section>
    <section class="panel stack-panel">
      <h3>Add a team member</h3>
      <p class="muted-sm">Enter their email address. They must sign in to the app with this address before access takes effect.</p>
      <form class="inline-form" onsubmit="return false">
        <input
          type="email"
          name="new_authorized_email"
          class="text-input"
          placeholder="colleague@example.com"
          ${busy ? "disabled" : ""}
        />
        <button type="button" class="primary-button" data-add-authorized-user ${busy ? "disabled" : ""}>${busy ? "Adding…" : "Add to team"}</button>
      </form>
    </section>
  `;
}

function renderTestAccountsPage(
  testingAccounts: TestingAccount[] | null,
  loading: boolean,
) {
  const rows = loading
    ? `<p class="muted">Loading…</p>`
    : testingAccounts === null
    ? `<p class="muted">Not loaded.</p>`
    : testingAccounts.length === 0
    ? `<p class="muted">No testing accounts yet.</p>`
    : `<table class="data-table">
        <thead><tr><th>Email</th><th>Added by</th><th>Added</th></tr></thead>
        <tbody>
          ${testingAccounts.map((a) => `
            <tr>
              <td>${escapeHtml(a.email)}</td>
              <td>${escapeHtml(a.added_by)}</td>
              <td>${escapeHtml(a.created_at.slice(0, 10))}</td>
            </tr>
          `).join("")}
        </tbody>
      </table>`;

  return `
    <h2 class="page-heading">Test accounts</h2>
    <section class="panel">
      <p class="muted-sm">Testing accounts can add tokens, clear batches, and reset profile data without going through billing. Only testing accounts can access this page or add new entries.</p>
      ${rows}
      <form class="test-account-add-form" style="margin-top:20px">
        <label class="field" style="max-width:360px">
          <span>Add testing account</span>
          <input name="new_testing_email" type="email" placeholder="email@example.com" autocomplete="off" />
        </label>
        <button type="button" class="primary-button" data-add-testing-account style="margin-top:8px">Add account</button>
      </form>
    </section>
  `;
}

function renderUploadPage(
  appData: AppData | null,
  csvResult: CsvValidationResult | null,
  csvFileName: string,
  lastSavedUploadId: string,
  selectedUploadDetails: UploadDetails | null,
  uploadAttested: boolean,
  uploadMode: "file" | "manual",
  sendStatus: "idle" | "sending" | "complete" | "failed" | "stopped" = "idle",
  sendResult: SendResult | null = null,
  isTesting = false,
) {
  const profileComplete = Boolean(appData?.profile?.is_profile_complete);
  const uploads = appData?.uploads ?? [];
  const canUpload = profileComplete && uploadAttested;
  const billingDone = isTesting ? true : Boolean(appData?.account?.square_card_id);
  const readyToSendCount = uploads
    .filter((u) => u.status === "validated")
    .reduce((sum, u) => sum + u.valid_rows, 0) +
    uploads.filter((u) => u.status === "ready")
    .reduce((sum, u) => sum + u.queued_rows, 0);

  return `
    <section class="panel stack-panel">
      <h3>Add phone numbers</h3>
      ${!profileComplete ? `<p class="muted">Complete your profile before uploading.</p>` : ""}
      <label class="attest-label ${profileComplete ? "" : "disabled"}">
        <input
          type="checkbox"
          data-attest-checkbox
          ${disabledAttr(!profileComplete)}
          ${uploadAttested ? "checked" : ""}
        />
        <span>I confirm that all phone numbers belong to customers who have consented
        to receive messages, and that consent records are held for at least 30 days.</span>
      </label>
      <div class="upload-tabs">
        <button
          class="upload-tab ${uploadMode === "file" ? "upload-tab-active" : ""}"
          data-upload-tab="file"
          ${disabledAttr(!profileComplete)}
        >Upload file</button>
        <button
          class="upload-tab ${uploadMode === "manual" ? "upload-tab-active" : ""}"
          data-upload-tab="manual"
          ${disabledAttr(!profileComplete)}
        >Type / paste numbers</button>
      </div>
      ${uploadMode === "file" ? `
        <p class="muted-sm">CSV, Excel (.xlsx), or plain text — one number per line. Mixed formats OK (dashes, parentheses, +1 all accepted). First column used for multi-column files.</p>
        <label class="upload-drop ${canUpload ? "" : "disabled"}">
          <span>Select file</span>
          <input type="file" accept=".csv,.xlsx,.xls,.txt,text/csv,text/plain" data-file-input ${disabledAttr(!canUpload)} />
        </label>
      ` : `
        <p class="muted-sm">Enter one phone number per line (or comma/semicolon-separated). Any format accepted.</p>
        <textarea
          class="manual-entry-textarea"
          data-manual-text
          rows="8"
          placeholder="6015551234&#10;(601) 555-5678&#10;+1 601-555-9012"
          ${disabledAttr(!canUpload)}
        ></textarea>
        <button class="primary-button" data-manual-add ${disabledAttr(!canUpload)}>Save as new batch</button>
      `}
    </section>
    <section class="panel stack-panel">
      <h3>Saved batches</h3>
      ${uploads.length === 0
        ? `<p>No batches yet.</p>`
        : `
          ${billingDone && readyToSendCount > 0 ? `
            <p class="muted-sm">Resend suppression is applied automatically before numbers are queued.</p>
            ${renderSendStatusBadge(sendStatus, sendResult)}
            <div class="welcome-actions">
              <button class="primary-button" data-send-all ${sendStatus === "sending" ? "disabled" : ""}>${sendStatus === "sending" ? "Sending…" : "Send now"}</button>
            </div>
          ` : !billingDone ? `
            <p class="muted-sm">Set up billing to start sending. <a href="/billing" data-nav>Set up billing →</a></p>
          ` : `
            <p class="muted-sm">No batches are ready to send. Upload and validate a batch to get started.</p>
          `}
          ${renderUploadBatchTable(uploads, selectedUploadDetails?.upload.id ?? lastSavedUploadId)}
        `
      }
    </section>
    ${
      csvResult
        ? `
          <section class="panel stack-panel">
            <h3>Validation results ${csvFileName ? `for ${escapeHtml(csvFileName)}` : ""}</h3>
            ${lastSavedUploadId ? `<p class="muted">Saved: ${escapeHtml(lastSavedUploadId)}</p>` : ""}
            <section class="grid compact-grid">
              ${renderMetric("Rows read", String(csvResult.summary.totalRows))}
              ${renderMetric("Valid", String(csvResult.summary.validRows))}
              ${renderMetric("Rejected", String(csvResult.summary.rejectedRows))}
            </section>
            ${renderValidationTable(csvResult)}
          </section>
        `
        : ""
    }
    ${selectedUploadDetails ? renderSelectedUploadDetails(selectedUploadDetails) : ""}
  `;
}

function renderBillingPage(appData: AppData | null, showUpdateForm: boolean, isGuest = false, isTesting = false) {
  const account = appData?.account;
  const currentPlanId = account?.usage_plan_id ?? usagePlans[0].id;
  const hasCard = Boolean(account?.square_card_id);
  const billingReadOnly = isGuest || isTesting;
  const showForm = !billingReadOnly && (!hasCard || showUpdateForm);

  const planCards = usagePlans.map((plan) => {
    const isCurrent = plan.id === currentPlanId;
    return `
      <div class="plan-card ${isCurrent ? "plan-card-active" : ""}">
        <div class="plan-card-name">${escapeHtml(plan.label)}</div>
        <div class="plan-card-price">$${plan.monthlyPrice}<span class="plan-card-period">/mo</span></div>
        <div class="plan-card-messages">${formatNumber(plan.includedMessages)} messages</div>
        ${isCurrent || billingReadOnly
          ? `<div class="plan-card-badge">Current plan</div>`
          : `<button class="secondary-button small-button plan-card-select" data-plan-id="${plan.id}">Select</button>`
        }
      </div>
    `;
  }).join("");

  return `
    <h2 class="page-heading">Billing</h2>
    ${isGuest ? `<div class="banner info" style="margin-bottom:16px">Billing is managed by the account owner. You can view plan details but cannot make changes.</div>` : ""}
    ${!isGuest && isTesting ? `<div class="banner info" style="margin-bottom:16px">Testing accounts bypass billing. You can view plan details, but payment setup is disabled while this account is marked for testing.</div>` : ""}

    <section class="panel stack-panel">
      <h3 class="panel-section-heading">Choose your plan</h3>
      <div class="plan-cards">${planCards}</div>
      <div class="token-balance-row">
        <span class="token-balance-label">Status</span>
        <span>${formatBillingStatus(account?.billing_status ?? "not_configured")}</span>
        ${account?.billing_status === "active" ? `
          <span class="token-balance-label" style="margin-left:16px">Tokens remaining</span>
          <span class="token-balance-value">${formatNumber(account.token_balance)}</span>
        ` : ""}
      </div>
    </section>

    <section class="panel stack-panel">
      <h3 class="panel-section-heading">Payment method</h3>

      ${hasCard && !showUpdateForm ? `
        <div class="billing-card-saved">
          <span class="billing-card-icon">💳</span>
          <div>
            <div class="billing-card-detail">${escapeHtml(account?.card_brand ?? "Card")} ending in ${escapeHtml(account?.card_last4 ?? "")}</div>
            <div class="billing-card-sub">Payment method on file</div>
          </div>
        </div>
        ${!billingReadOnly ? `<button class="secondary-button small-button" style="width:fit-content" data-update-card>Update card</button>` : ""}
      ` : ""}

      ${showForm ? `
        <p style="margin:0 0 10px">
          ${hasCard ? "Enter new card details to replace your saved payment method." : "Add a card to activate your plan."}
        </p>
        <div id="square-card-container" class="square-card-container"></div>
        <div id="billing-error" class="banner error" style="display:none;margin-top:12px"></div>
        <button class="primary-button" style="margin-top:14px" data-billing-submit>Save payment method</button>
      ` : ""}
    </section>
  `;
}

function renderLoadingPanel() {
  return `
    <section class="panel">
      <h3>Loading data</h3>
      <p>Fetching business account state from Firebase.</p>
    </section>
  `;
}

function renderValidationTable(result: CsvValidationResult) {
  return `
    <div class="table-wrapper">
      <div class="table-toolbar">
        <button class="secondary-button small-button" data-toggle-raw>Show original numbers</button>
      </div>
      <table>
        <thead>
          <tr>
            <th>Row</th>
            <th>Phone (E.164)</th>
            <th class="raw-col">Original</th>
            <th>Status</th>
            <th>Reason</th>
          </tr>
        </thead>
        <tbody>
          ${result.rows
            .map(
              (row) => `
                <tr>
                  <td>${row.row_number}</td>
                  <td>${escapeHtml(row.normalized_phone_number ?? row.phone_number)}</td>
                  <td class="raw-col">${escapeHtml(row.phone_number)}</td>
                  <td>${escapeHtml(formatRowStatus(row.status))}</td>
                  <td>${escapeHtml(row.rejection_reason ?? "")}</td>
                </tr>
              `,
            )
            .join("")}
        </tbody>
      </table>
    </div>
  `;
}

function renderUploadBatchTable(uploads: UploadRecord[], selectedUploadId: string) {
  return `
    <div class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>File</th>
            <th>Status</th>
            <th>Total</th>
            <th>Ready/queued</th>
            <th>Rejected</th>
            <th>Suppressed</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          ${uploads
            .map(
              (upload) => `
                <tr class="${upload.id === selectedUploadId ? "selected-row" : ""}">
                  <td>${escapeHtml(upload.original_filename)}</td>
                  <td>${escapeHtml(formatUploadStatus(upload.status))}</td>
                  <td>${upload.total_rows}</td>
                  <td>${upload.status === "ready" ? upload.queued_rows : upload.valid_rows}</td>
                  <td>${upload.rejected_rows}</td>
                  <td>${upload.suppressed_rows}</td>
                  <td>${formatDate(upload.created_at)}</td>
                  <td>
                    <div class="table-actions">
                      <button class="secondary-button small-button" type="button" data-select-upload data-upload-id="${escapeHtml(upload.id)}">
                        Review
                      </button>
                    </div>
                  </td>
                </tr>
              `,
            )
            .join("")}
        </tbody>
      </table>
    </div>
  `;
}

function renderSelectedUploadDetails(details: UploadDetails) {
  const editable = details.upload.status === "validated";
  const queuedCount = details.rows.filter((r) => r.status === "queued").length;
  const processedStatuses: UploadRow["status"][] = ["optin_sent", "optin_1_sent", "sent", "failed", "skipped_opted_out", "skipped_recent"];
  const allProcessed = details.rows.length > 0 && queuedCount === 0 && details.rows.every((r) => r.status !== "queued" && r.status !== "valid");
  const noneQueued = !editable && queuedCount === 0 && details.rows.some((r) => processedStatuses.includes(r.status));

  return `
    <section class="panel stack-panel">
      <h3>Batch: ${escapeHtml(details.upload.original_filename)}</h3>
      ${noneQueued ? `
        <div class="banner error" style="margin:0">
          No rows are queued — all numbers in this batch have already been processed.
          Sending now would send 0 messages. To re-test a number, clear batch history first.
        </div>
      ` : ""}
      ${renderBatchStatusBreakdown(details.rows)}
      ${editable
        ? `<p class="muted-sm">Not yet queued — you can add or remove numbers before starting.</p>`
        : ""
      }
      ${renderUploadRowsTable(details.rows, editable, details.upload.id)}
      ${editable ? `
        <div class="add-more-section">
          <h4>Add more numbers to this batch</h4>
          <p class="muted-sm">One per line, or comma/semicolon-separated.</p>
          <textarea class="manual-entry-textarea" data-add-more-text rows="4" placeholder="6015551234&#10;(601) 555-5678"></textarea>
          <button class="secondary-button" data-add-more-rows="${escapeHtml(details.upload.id)}">Add to batch</button>
        </div>
      ` : ""}
    </section>
  `;
}

function renderUploadRowsTable(rows: UploadRow[], editable = false, uploadId = "") {
  return `
    <div class="table-wrapper">
      <div class="table-toolbar">
        <button class="secondary-button small-button" data-toggle-raw>Show original numbers</button>
      </div>
      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>Phone (E.164)</th>
            <th class="raw-col">Original</th>
            <th>Send pipeline</th>
            <th>Queued at</th>
            ${editable ? "<th></th>" : ""}
          </tr>
        </thead>
        <tbody>
          ${rows
            .map(
              (row) => `
                <tr>
                  <td>${row.row_number}</td>
                  <td>${escapeHtml(row.normalized_phone_number ?? row.phone_number)}</td>
                  <td class="raw-col">${escapeHtml(row.phone_number)}</td>
                  <td>${renderRowPipeline(effectiveRowStatus(row))}</td>
                  <td class="muted-sm">${row.queued_at ? formatDate(row.queued_at) : ""}</td>
                  ${editable && row.id ? `
                    <td>
                      <button
                        class="row-delete-btn"
                        type="button"
                        data-remove-row="${escapeHtml(uploadId)}"
                        data-row-id="${escapeHtml(row.id)}"
                        title="Remove row"
                      >✕</button>
                    </td>
                  ` : editable ? "<td></td>" : ""}
                </tr>
              `,
            )
            .join("")}
        </tbody>
      </table>
    </div>
  `;
}

function renderUploadsTable(
  uploads: Array<{
    original_filename: string;
    total_rows: number;
    valid_rows: number;
    rejected_rows: number;
    queued_rows?: number;
    suppressed_rows?: number;
    created_at: string;
  }>,
) {
  return `
    <div class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>File</th>
            <th>Total rows</th>
            <th>Valid</th>
            <th>Rejected</th>
            <th>Created</th>
          </tr>
        </thead>
        <tbody>
          ${uploads
            .map(
              (upload) => `
                <tr>
                  <td>${escapeHtml(upload.original_filename)}</td>
                  <td>${upload.total_rows}</td>
                  <td>${upload.valid_rows}</td>
                  <td>${upload.rejected_rows}</td>
                  <td>${formatDate(upload.created_at)}</td>
                </tr>
              `,
            )
            .join("")}
        </tbody>
      </table>
    </div>
  `;
}

function renderMetric(label: string, value: string) {
  return `
    <article class="metric-card">
      <div class="metric-label">${label}</div>
      <div class="metric-value">${escapeHtml(value)}</div>
    </article>
  `;
}

function renderUsagePlanOptions(account: BusinessAccount | null | undefined) {
  const selectedPlanId = account?.usage_plan_id ?? usagePlans[0].id;

  return usagePlans
    .map(
      (plan) => `
        <option value="${plan.id}" ${plan.id === selectedPlanId ? "selected" : ""}>
          ${escapeHtml(plan.label)} - $${plan.monthlyPrice} / month for ${formatNumber(plan.includedMessages)} messages
        </option>
      `,
    )
    .join("");
}

function getUsagePlan(planId: UsagePlanId | null | undefined) {
  return usagePlans.find((plan) => plan.id === planId) ?? usagePlans[0];
}

function usagePlanIdFromForm(value: FormDataEntryValue | null): UsagePlanId {
  return value === "growth_1000" || value === "scale_5000" || value === "starter_250"
    ? value
    : usagePlans[0].id;
}

function formatNumber(value: number) {
  return new Intl.NumberFormat("en-US").format(value);
}

function formatBillingStatus(value: string) {
  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatUploadStatus(value: UploadRecord["status"]) {
  if (value === "ready") {
    return "Ready / queued";
  }
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function formatRowStatus(value: UploadRow["status"]) {
  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

type PipeStep = { label: string; state: "done" | "fail" | "warn" };

function rowPipelineSteps(status: UploadRow["status"]): PipeStep[] {
  const d = (label: string): PipeStep => ({ label, state: "done" });
  const f = (label: string): PipeStep => ({ label, state: "fail" });
  const w = (label: string): PipeStep => ({ label, state: "warn" });
  switch (status) {
    case "valid":
      return [d("Imported"), d("Valid"), w("Not yet queued")];
    case "queued":
      return [d("Imported"), d("Valid"), d("Queued"), w("Ready to send")];
    case "optin_1_sent":
      return [d("Imported"), d("Valid"), d("Queued"), d("Pt. 1 sent"), w("Pt. 2 sending...")];
    case "optin_sent":
      return [d("Imported"), d("Valid"), d("Queued"), d("Pt. 1 sent"), d("Pt. 2 sent"), w("Awaiting reply")];
    case "sent":
      return [d("Imported"), d("Valid"), d("Queued"), d("Pt. 1 sent"), d("Pt. 2 sent"), d("Replied")];
    case "failed":
      return [d("Imported"), d("Valid"), d("Queued"), f("Send failed")];
    case "rejected_invalid":
      return [d("Imported"), f("Invalid number")];
    case "rejected_duplicate":
      return [d("Imported"), f("Duplicate number")];
    case "rejected_quota":
      return [d("Imported"), f("Quota exceeded")];
    case "rejected_suppressed":
      return [d("Imported"), d("Valid"), f("Suppressed (prior opt-out)")];
    case "skipped_opted_out":
      return [d("Imported"), d("Valid"), d("Queued"), f("Skipped — opted out")];
    case "skipped_recent":
      return [d("Imported"), d("Valid"), d("Queued"), f("Skipped — contacted < 1yr")];
    case "no_phone":
      return [d("Imported"), f("No valid phone number")];
    case "completed_yes":
      return [d("Imported"), d("Valid"), d("Queued"), d("Pt. 1 sent"), d("Pt. 2 sent"), d("Replied YES"), w("Awaiting click")];
    case "completed_no":
      return [d("Imported"), d("Valid"), d("Queued"), d("Pt. 1 sent"), d("Pt. 2 sent"), d("Replied NO"), w("Awaiting feedback")];
    case "opted_out":
      return [d("Imported"), d("Valid"), d("Queued"), d("Pt. 1 sent"), d("Pt. 2 sent"), f("Opted out (STOP)")];
    case "clicked_review":
      return [d("Imported"), d("Valid"), d("Queued"), d("Pt. 1 sent"), d("Pt. 2 sent"), d("Replied YES"), d("Clicked review link")];
    case "clicked_form":
      return [d("Imported"), d("Valid"), d("Queued"), d("Pt. 1 sent"), d("Pt. 2 sent"), d("Replied NO"), d("Clicked feedback form"), w("Awaiting submission")];
    case "form_submitted":
      return [d("Imported"), d("Valid"), d("Queued"), d("Pt. 1 sent"), d("Pt. 2 sent"), d("Replied NO"), d("Clicked feedback form"), d("Form submitted")];
    default:
      return [d("Imported"), w(status)];
  }
}

// effectiveRowStatus prefers the number's live conversation status (attached at
// read time) over the frozen per-row mirror, so every batch shows what is
// actually happening with the number and agrees with the activity log (#36).
function effectiveRowStatus(row: UploadRow): UploadRow["status"] {
  return row.live_status ?? row.status;
}

function renderRowPipeline(status: UploadRow["status"]): string {
  const steps = rowPipelineSteps(status);
  const parts = steps.flatMap((step, i) => {
    const chip = `<span class="pipe-step pipe-${step.state}">${escapeHtml(step.label)}</span>`;
    return i < steps.length - 1 ? [chip, `<span class="pipe-sep">›</span>`] : [chip];
  });
  return `<div class="row-pipeline">${parts.join("")}</div>`;
}

function renderBatchStatusBreakdown(rows: UploadRow[]): string {
  const counts: Partial<Record<UploadRow["status"], number>> = {};
  for (const row of rows) {
    const st = effectiveRowStatus(row);
    counts[st] = (counts[st] ?? 0) + 1;
  }

  const groups: { label: string; statuses: UploadRow["status"][]; cls: string }[] = [
    { label: "Clicked review link", statuses: ["clicked_review"], cls: "bk-done" },
    { label: "Form submitted", statuses: ["form_submitted"], cls: "bk-done" },
    { label: "Clicked feedback form", statuses: ["clicked_form"], cls: "bk-warn" },
    { label: "Awaiting reply", statuses: ["optin_sent"], cls: "bk-warn" },
    { label: "Replied YES — awaiting click", statuses: ["completed_yes"], cls: "bk-warn" },
    { label: "Replied NO — awaiting feedback", statuses: ["completed_no"], cls: "bk-warn" },
    { label: "Queued — ready to send", statuses: ["queued"], cls: "bk-queued" },
    { label: "Opt-in 1 sent (pt. 2 pending)", statuses: ["optin_1_sent"], cls: "bk-warn" },
    { label: "Not yet queued", statuses: ["valid"], cls: "bk-neutral" },
    { label: "Send failed", statuses: ["failed"], cls: "bk-fail" },
    { label: "Opted out (STOP)", statuses: ["opted_out", "skipped_opted_out"], cls: "bk-skip" },
    { label: "Skipped — contacted < 1yr", statuses: ["skipped_recent"], cls: "bk-skip" },
    { label: "Suppressed (prior opt-out)", statuses: ["rejected_suppressed"], cls: "bk-skip" },
    { label: "Invalid number", statuses: ["rejected_invalid", "no_phone"], cls: "bk-fail" },
    { label: "Duplicate", statuses: ["rejected_duplicate"], cls: "bk-fail" },
    { label: "Quota exceeded", statuses: ["rejected_quota"], cls: "bk-fail" },
  ];

  const items = groups
    .map((g) => {
      const n = g.statuses.reduce((sum, s) => sum + (counts[s] ?? 0), 0);
      if (n === 0) return "";
      return `<div class="bk-item ${g.cls}"><span class="bk-count">${n}</span> ${escapeHtml(g.label)}</div>`;
    })
    .filter(Boolean)
    .join("");

  if (!items) return "";
  return `<div class="batch-status-breakdown">${items}</div>`;
}


function renderBanner(state: { notice: string; error: string }) {
  const items = [
    state.error ? `<div class="banner error">${escapeHtml(state.error)}</div>` : "",
    state.notice ? `<div class="banner notice">${escapeHtml(state.notice)}</div>` : "",
  ].filter(Boolean);

  return items.join("");
}

function disabledAttr(disabled: boolean) {
  return disabled ? "disabled" : "";
}

function readError(error: unknown) {
  if (error instanceof Error) {
    return error.message;
  }

  return "An unexpected error occurred.";
}

function formatDate(value: string) {
  try {
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(value));
  } catch {
    return value;
  }
}

function escapeHtml(value: string) {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}
